import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  getAutomations, updateAutomation, createAutomation, setAutomationEnabled, searchAutomationTopics,
  listAutomationDestinations, createAutomationDestination, updateAutomationDestination, deleteAutomationDestination,
  startAutomationTestRun, getAutomationRun,
  AutomationRow, AutomationsResponse, JobSpecLite, TopicHit,
  AutomationDestination, DestinationInput, AutomationRun,
  DestinationKind, AnyDestinationInput, CustomApiDestinationInput,
} from '../../services/auth';

const HEALTH_COLOR: Record<string, string> = { ok: '#22c55e', warn: '#f59e0b', error: '#ef4444' };
const HEALTH_LABEL: Record<string, string> = { ok: 'OK', warn: 'Attention', error: 'Error' };

// Fixed vocabularies (Scott: fixed-value filters should be dropdowns/pickers, not free text).
const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];
const SCORE_CATEGORIES = ['high', 'medium', 'low'];
const AGE_BUCKETS = ['18-24', '25-34', '35-44', '45-54', '55-64', '65 and older'];
// Gender values are the exact strings RetargetIQ's b2c table stores (confirmed via probe: 'f'/'m').
const GENDERS = [{ label: 'Female', value: 'f' }, { label: 'Male', value: 'm' }];
// income_range: the exact vendor value-strings (incl. their '$200,000 to $249,000' typo).
const INCOME_RANGES = ['Less than $20,000', '$20,000 to $44,999', '$45,000 to $59,999', '$60,000 to $74,999', '$75,000 to $99,999', '$100,000 to $149,999', '$150,000 to $199,999', '$200,000 to $249,000', '$250,000+'];
const NET_WORTH_RANGES = ['-$20,000 to -$2,500', '-$2,499 to $2,499', '$2,500 to $24,999', '$25,000 to $49,999', '$50,000 to $74,999', '$75,000 to $99,999', '$100,000 to $149,999', '$150,000 to $249,999', '$250,000 to $374,999', '$375,000 to $499,999', '$500,000 to $749,999', '$750,000 to $999,999', 'More than $1,000,000'];
const EMAIL_VALIDATION = ['valid (esp)'];

const fmtNum = (n: number | null) => (n == null ? '—' : n.toLocaleString());
function fmtWhen(iso: string | null): string {
  if (!iso) {return 'never';}
  const d = new Date(iso);
  const days = (Date.now() - d.getTime()) / 8.64e7;
  const date = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return days > 1 ? `${date} (${Math.floor(days)}d ago)` : `${date} ${time}`;
}
const toNum = (s: string, fallback = 0): number => { const n = Number(s); return Number.isFinite(n) ? n : fallback; };

function Badge({ children, color }: { children: React.ReactNode; color: string }) {
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700,
      color, background: `${color}22`, border: `1px solid ${color}55`, whiteSpace: 'nowrap',
    }}>{children}</span>
  );
}

const th: React.CSSProperties = {
  textAlign: 'left', padding: '10px 12px', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4,
  color: '#9ca3af', borderBottom: '1px solid var(--border, #2a2f3a)', whiteSpace: 'nowrap', position: 'sticky', top: 0,
  background: 'var(--card-bg, #1a1d24)',
};
const td: React.CSSProperties = { padding: '10px 12px', fontSize: 13, color: '#e5e7eb', borderBottom: '1px solid var(--border, #23272f)', verticalAlign: 'top' };

// ---- modal form styling ----
const label: React.CSSProperties = { display: 'block', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, color: '#9ca3af', marginBottom: 4 };
const input: React.CSSProperties = { width: '100%', padding: '8px 10px', fontSize: 13, color: '#e5e7eb', background: '#0f1218', border: '1px solid #2a2f3a', borderRadius: 6, boxSizing: 'border-box' };
const field: React.CSSProperties = { marginBottom: 14 };
const sectionHead: React.CSSProperties = { fontSize: 12, fontWeight: 700, color: '#cbd5e1', textTransform: 'uppercase', letterSpacing: 0.5, margin: '18px 0 10px', borderTop: '1px solid #23272f', paddingTop: 14 };

// ============================================================
// TopicPicker — typeahead against RetargetIQ (label → id). Selected topics are chips; the search
// box debounces and calls the admin proxy. Replaces hand-copying ids from resolveTopics.js.
// ============================================================
function TopicPicker({ value, onChange, search }: {
  value: TopicHit[];
  onChange: (next: TopicHit[]) => void;
  search: (q: string) => Promise<TopicHit[]>;
}) {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<TopicHit[]>([]);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    const term = q.trim();
    if (!term) { setHits([]); setMsg(''); return; }
    let cancelled = false;
    setBusy(true);
    const t = setTimeout(async () => {
      try {
        const r = await search(term);
        if (!cancelled) { setHits(r); setOpen(true); setMsg(r.length ? '' : 'no matches'); }
      } catch {
        if (!cancelled) { setHits([]); setMsg('topic search unavailable'); }
      } finally {
        if (!cancelled) {setBusy(false);}
      }
    }, 300); // debounce keystrokes
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, search]);

  const add = (h: TopicHit) => {
    if (!value.some((v) => v.id === h.id)) {onChange([...value, h]);}
    setQ(''); setHits([]); setOpen(false);
  };
  const remove = (id: string) => onChange(value.filter((v) => v.id !== id));

  return (
    <div>
      {value.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
          {value.map((t) => (
            <span key={t.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 8px', borderRadius: 999, background: '#1d4ed822', border: '1px solid #2563eb55', color: '#93c5fd', fontSize: 12 }}>
              {t.label} <span style={{ color: '#6b7280', fontFamily: 'monospace' }}>#{t.id}</span>
              <button type="button" onClick={() => remove(t.id)} style={{ background: 'none', border: 'none', color: '#93c5fd', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0 }}>×</button>
            </span>
          ))}
        </div>
      )}
      <div style={{ position: 'relative' }}>
        <input
          style={input}
          value={q}
          placeholder="Search topics (e.g. Auto Accident)…"
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => hits.length && setOpen(true)}
        />
        {(busy || msg) && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3 }}>{busy ? 'searching…' : msg}</div>}
        {open && hits.length > 0 && (
          <div style={{ position: 'absolute', zIndex: 10, top: '100%', left: 0, right: 0, marginTop: 4, background: '#0f1218', border: '1px solid #2a2f3a', borderRadius: 6, maxHeight: 220, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.5)' }}>
            {hits.map((h) => (
              <button
                key={h.id} type="button" onClick={() => add(h)}
                style={{ display: 'flex', justifyContent: 'space-between', width: '100%', padding: '8px 10px', background: 'none', border: 'none', borderBottom: '1px solid #1c2029', color: '#e5e7eb', fontSize: 13, cursor: 'pointer', textAlign: 'left' }}
              >
                <span>{h.label}</span>
                <span style={{ color: '#6b7280', fontFamily: 'monospace' }}>#{h.id}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// Job destination: Data Body import (fields) or a saved Custom API destination (by id).
// ============================================================
type JobDestKind = 'data_body' | 'custom_api';
function DeliverToPicker({ kind, setKind, apiDestId, setApiDestId, destinations }: {
  kind: JobDestKind; setKind: (k: JobDestKind) => void;
  apiDestId: string; setApiDestId: (id: string) => void;
  destinations: AutomationDestination[];
}) {
  const apis = destinations.filter((d) => d.kind === 'custom_api');
  return (
    <>
      <div style={field}>
        <label style={label}>Deliver to</label>
        <select style={input} value={kind} onChange={(e) => setKind(e.target.value as JobDestKind)}>
          <option value="data_body">Data Body (import into a body)</option>
          <option value="custom_api">Custom API (push each lead to an endpoint)</option>
        </select>
      </div>
      {kind === 'custom_api' && (
        <div style={field}>
          <label style={label}>Custom API destination *</label>
          <select style={input} value={apiDestId} onChange={(e) => setApiDestId(e.target.value)}>
            <option value="">{apis.length ? 'Pick one…' : 'No Custom API destinations yet'}</option>
            {apis.map((d) => {
              const h = (() => { try { return new URL(d.apiConfig?.webhookUrl ?? '').host; } catch { return d.apiConfig?.webhookUrl ?? ''; } })();
              return <option key={d.id} value={d.id}>{d.name} ({h})</option>;
            })}
          </select>
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3 }}>Add or edit these in the Destinations registry on the board. Edits there apply to every job using it.</div>
        </div>
      )}
    </>
  );
}

// ============================================================
// Edit modal — edits a job's full spec and PATCHes it back. Works on a deep clone of the
// row's spec, so any field the form doesn't surface (advanced grid/segsweep knobs, fields[],
// staticFields, etc.) round-trips untouched. Backend validateSpec is the real gate; a 400
// surfaces here as an inline error instead of closing the modal.
// ============================================================
function EditAutomationModal({ row, clients, destinations, onClose, onSave, searchTopics }: {
  row: AutomationRow;
  clients: string[];   // existing client names, for the picker's suggestions
  destinations: AutomationDestination[];   // registry (migration 033) for the destination picker
  onClose: () => void;
  onSave: (spec: JobSpecLite) => Promise<void>;
  searchTopics: (q: string) => Promise<TopicHit[]>;
}) {
  const spec = row.spec;
  const isIntent = spec.mode.kind === 'intent';
  // Delivery is editable for data-body imports and custom API jobs (they can switch between the two).
  const destEditable = spec.destination.type === 'data_body_import' || spec.destination.type === 'custom_api';
  const [destKind, setDestKind] = useState<JobDestKind>(spec.destination.type === 'custom_api' ? 'custom_api' : 'data_body');
  const [apiDestId, setApiDestId] = useState<string>(spec.destination.destinationId ?? '');

  // Common fields
  const [enabled, setEnabled] = useState<boolean>(spec.enabled);
  // Client is an override: blank = derive from the job name (row.client shows the effective value).
  const [client, setClient] = useState<string>(spec.client ?? '');
  const [schedule, setSchedule] = useState<string>(spec.schedule ?? '');
  const [freshnessDays, setFreshnessDays] = useState<string>(String(spec.freshnessDays ?? 0));
  const [dailyBudget, setDailyBudget] = useState<string>(String(spec.dailyBudget ?? 0));
  const [frequency, setFrequency] = useState<'daily' | 'weekly'>(spec.frequency === 'weekly' ? 'weekly' : 'daily');

  // Intent-mode fields
  const m: any = spec.mode;
  // Zip existing labels+ids into topic chips (labels are positional to ids).
  const [topics, setTopics] = useState<TopicHit[]>(
    ((m.topicIds ?? []) as string[]).map((id: string, i: number) => ({ id, label: (m.topicLabels ?? [])[i] ?? id })),
  );
  const [score, setScore] = useState<string[]>(m.scoreCategories ?? []);
  const [lookbackDays, setLookbackDays] = useState<string>(m.lookbackDays != null ? String(m.lookbackDays) : '');
  const [requireEmail, setRequireEmail] = useState<boolean>(!!m.requireEmail);
  const [requireMobile, setRequireMobile] = useState<boolean>(!!m.requireMobile);
  const [emailLastSeen, setEmailLastSeen] = useState<string>(m.emailLastSeenDays != null ? String(m.emailLastSeenDays) : '');
  const [states, setStates] = useState<string[]>(Array.isArray(m.states) ? m.states : []); // [] = all states
  const [cities, setCities] = useState<string>(Array.isArray(m.cities) ? m.cities.join(', ') : ''); // '' = no city filter
  const [ageRanges, setAgeRanges] = useState<string[]>(Array.isArray(m.ageRanges) ? m.ageRanges : []); // [] = all ages
  const [gender, setGender] = useState<string[]>(Array.isArray(m.gender) ? m.gender : []); // [] = any gender
  const [incomeRanges, setIncomeRanges] = useState<string[]>(Array.isArray(m.incomeRanges) ? m.incomeRanges : []); // [] = all incomes
  const [netWorthRanges, setNetWorthRanges] = useState<string[]>(Array.isArray(m.netWorthRanges) ? m.netWorthRanges : []); // [] = all net worth
  const [emailValidation, setEmailValidation] = useState<string[]>(Array.isArray(m.emailValidation) ? m.emailValidation : []); // [] = any validation

  // Destination fields
  const [dataBodyId, setDataBodyId] = useState<string>(spec.destination.dataBodyId ?? '');
  const [apiBaseUrl, setApiBaseUrl] = useState<string>(spec.destination.apiBaseUrl ?? '');
  const [loginEmailEnv, setLoginEmailEnv] = useState<string>((spec.destination as any).loginEmailEnv ?? '');
  const [loginPasswordEnv, setLoginPasswordEnv] = useState<string>((spec.destination as any).loginPasswordEnv ?? '');

  // Pick a registry destination → fill all four coupled fields at once.
  const applyDestination = (id: string) => {
    const d = destinations.find((x) => x.id === id);
    if (!d) {return;}
    setDataBodyId(d.dataBodyId ?? '');
    setApiBaseUrl(d.apiBaseUrl ?? '');
    setLoginEmailEnv(d.loginEmailEnv ?? '');
    setLoginPasswordEnv(d.loginPasswordEnv ?? '');
  };

  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const toggleScore = (s: string) => setScore((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));
  const toggleState = (s: string) => setStates((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));
  const toggleAge = (a: string) => setAgeRanges((cur) => (cur.includes(a) ? cur.filter((x) => x !== a) : [...cur, a]));
  const toggleGender = (g: string) => setGender((cur) => (cur.includes(g) ? cur.filter((x) => x !== g) : [...cur, g]));
  const toggleIncome = (i: string) => setIncomeRanges((cur) => (cur.includes(i) ? cur.filter((x) => x !== i) : [...cur, i]));
  const toggleNetWorth = (n: string) => setNetWorthRanges((cur) => (cur.includes(n) ? cur.filter((x) => x !== n) : [...cur, n]));
  const toggleEmailValidation = (v: string) => setEmailValidation((cur) => (cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]));

  function buildSpec(): JobSpecLite {
    const next: JobSpecLite = JSON.parse(JSON.stringify(spec)); // deep clone → preserve untouched fields
    next.enabled = enabled;
    if (client.trim()) {next.client = client.trim();} else {delete next.client;} // blank = derive from name

    if (schedule.trim()) {next.schedule = schedule.trim();} else {delete next.schedule;}
    next.frequency = frequency;
    next.freshnessDays = toNum(freshnessDays, spec.freshnessDays);
    next.dailyBudget = toNum(dailyBudget, spec.dailyBudget);
    if (isIntent) {
      next.mode.topicLabels = topics.map((t) => t.label);
      next.mode.topicIds = topics.map((t) => t.id);
      next.mode.scoreCategories = score;
      next.mode.lookbackDays = toNum(lookbackDays, m.lookbackDays ?? 1);
      next.mode.requireEmail = requireEmail;
      next.mode.requireMobile = requireMobile;
      if (emailLastSeen.trim()) {next.mode.emailLastSeenDays = toNum(emailLastSeen);} else {delete next.mode.emailLastSeenDays;}
      if (states.length) {next.mode.states = states;} else {delete next.mode.states;} // [] = all
      const cityList = cities.split(',').map((c) => c.trim()).filter(Boolean);
      if (gender.length) {next.mode.gender = gender;} else {delete next.mode.gender;} // [] = any
      if (incomeRanges.length) {next.mode.incomeRanges = incomeRanges;} else {delete next.mode.incomeRanges;} // [] = all
      if (netWorthRanges.length) {next.mode.netWorthRanges = netWorthRanges;} else {delete next.mode.netWorthRanges;} // [] = all
      if (emailValidation.length) {next.mode.emailValidation = emailValidation;} else {delete next.mode.emailValidation;} // [] = any
      if (cityList.length) {next.mode.cities = cityList;} else {delete next.mode.cities;} // '' = no city filter
      if (ageRanges.length) {next.mode.ageRanges = ageRanges;} else {delete next.mode.ageRanges;} // [] = all ages
    }
    if (destEditable && destKind === 'custom_api') {
      if (!apiDestId) {throw new Error('Pick a Custom API destination.');}
      next.destination = { type: 'custom_api', destinationId: apiDestId };
    } else if (destEditable) {
      if (!dataBodyId.trim()) {throw new Error('Destination body UUID is required.');}
      next.destination = { ...next.destination, type: 'data_body_import' };
      delete next.destination.destinationId;
      next.destination.dataBodyId = dataBodyId.trim();
      if (apiBaseUrl.trim()) {next.destination.apiBaseUrl = apiBaseUrl.trim();} else {delete next.destination.apiBaseUrl;}
      if (loginEmailEnv.trim()) {next.destination.loginEmailEnv = loginEmailEnv.trim();} else {delete next.destination.loginEmailEnv;}
      if (loginPasswordEnv.trim()) {next.destination.loginPasswordEnv = loginPasswordEnv.trim();} else {delete next.destination.loginPasswordEnv;}
    }
    return next;
  }

  const handleSave = async () => {
    setSaving(true); setErr('');
    try {
      await onSave(buildSpec());
      onClose();
    } catch (e: any) {
      setErr(e?.response?.data?.error || e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, overflowY: 'auto', padding: '5vh 16px' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ width: 560, maxWidth: '100%', background: 'var(--card-bg, #1a1d24)', border: '1px solid #2a2f3a', borderRadius: 12, padding: 24, boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
          <h2 style={{ margin: 0, fontSize: 18, color: '#e5e7eb' }}>Edit automation</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ fontFamily: 'monospace', fontSize: 12, color: '#9ca3af', marginBottom: 6 }}>{spec.name}</div>
        <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 8 }}>
          {row.client} · mode <b style={{ color: '#cbd5e1' }}>{spec.mode.kind}</b> · dest <b style={{ color: '#cbd5e1' }}>{spec.destination.type}</b>
        </div>

        {/* Enabled — the safety gate */}
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', margin: '14px 0' }}>
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          <span style={{ color: '#e5e7eb', fontWeight: 600 }}>Enabled</span>
          <span style={{ fontSize: 12, color: '#9ca3af' }}>— when off, this job is skipped by the nightly pull</span>
        </label>

        <div style={field}>
          <label style={label}>Client</label>
          <input
            style={input}
            list="automation-clients"
            value={client}
            placeholder={`${row.client} (derived from job name)`}
            onChange={(e) => setClient(e.target.value)}
          />
          <datalist id="automation-clients">
            {clients.map((c) => <option key={c} value={c} />)}
          </datalist>
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3 }}>Pick an existing client or type a new one. Leave blank to derive from the job name.</div>
        </div>

        <div style={sectionHead}>Schedule & limits</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div style={field}>
            <label style={label}>Frequency</label>
            <select style={input} value={frequency} onChange={(e) => setFrequency(e.target.value as 'daily' | 'weekly')}>
              <option value="daily">Daily (8 AM PT)</option>
              <option value="weekly">Weekly (Wed 8 AM PT)</option>
            </select>
          </div>
          <div style={field}>
            <label style={label}>Schedule (display label)</label>
            <input style={input} value={schedule} placeholder="8 AM PT" onChange={(e) => setSchedule(e.target.value)} />
          </div>
          <div style={field}>
            <label style={label}>Freshness (days)</label>
            <input style={input} type="number" value={freshnessDays} onChange={(e) => setFreshnessDays(e.target.value)} />
          </div>
          <div style={field}>
            <label style={label}>Daily cap (rows)</label>
            <input style={input} type="number" value={dailyBudget} onChange={(e) => setDailyBudget(e.target.value)} />
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3 }}>0 = no cap</div>
          </div>
        </div>

        {isIntent && (
          <>
            <div style={sectionHead}>Topic & intent filters</div>
            <div style={field}>
              <label style={label}>Topics</label>
              <TopicPicker value={topics} onChange={setTopics} search={searchTopics} />
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3 }}>Search RetargetIQ by label; the vendor topic id is resolved automatically.</div>
            </div>
            <div style={field}>
              <label style={label}>Score categories</label>
              <div style={{ display: 'flex', gap: 14 }}>
                {SCORE_CATEGORIES.map((s) => (
                  <label key={s} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: '#e5e7eb', fontSize: 13 }}>
                    <input type="checkbox" checked={score.includes(s)} onChange={() => toggleScore(s)} />{s}
                  </label>
                ))}
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div style={field}>
                <label style={label}>Intent lookback (days)</label>
                <input style={input} type="number" value={lookbackDays} placeholder="1" onChange={(e) => setLookbackDays(e.target.value)} />
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3 }}>1 = today only</div>
              </div>
              <div style={field}>
                <label style={label}>Email last-seen ≤ (days)</label>
                <input style={input} type="number" value={emailLastSeen} placeholder="(none)" onChange={(e) => setEmailLastSeen(e.target.value)} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 20, ...field }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: '#e5e7eb', fontSize: 13 }}>
                <input type="checkbox" checked={requireEmail} onChange={(e) => setRequireEmail(e.target.checked)} />require email
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: '#e5e7eb', fontSize: 13 }}>
                <input type="checkbox" checked={requireMobile} onChange={(e) => setRequireMobile(e.target.checked)} />require mobile
              </label>
            </div>
            <div style={field}>
              <label style={label}>States ({states.length === 0 ? 'all states' : `${states.length} selected`})</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(9, 1fr)', gap: 4, marginBottom: 6 }}>
                {US_STATES.map((s) => {
                  const on = states.includes(s);
                  return (
                    <button
                      key={s} type="button" onClick={() => toggleState(s)}
                      style={{
                        padding: '4px 0', fontSize: 11, borderRadius: 4, cursor: 'pointer',
                        border: `1px solid ${on ? '#2563eb' : '#2a2f3a'}`,
                        background: on ? '#1d4ed8' : '#0f1218', color: on ? '#fff' : '#9ca3af', fontWeight: on ? 700 : 400,
                      }}
                    >{s}</button>
                  );
                })}
              </div>
              <div style={{ display: 'flex', gap: 10, fontSize: 11 }}>
                <button type="button" onClick={() => setStates([])} style={{ background: 'none', border: 'none', color: '#60a5fa', cursor: 'pointer', padding: 0 }}>clear (= all)</button>
                <button type="button" onClick={() => setStates(US_STATES.filter((s) => s !== 'CA' && s !== 'DC'))} style={{ background: 'none', border: 'none', color: '#60a5fa', cursor: 'pointer', padding: 0 }}>49 (excl. CA, DC)</button>
                <span style={{ color: '#6b7280' }}>DC 422s at the vendor; CA usually excluded.</span>
              </div>
            </div>
            <div style={field}>
              <label style={label}>Cities</label>
              <input style={input} value={cities} placeholder="(no city filter)" onChange={(e) => setCities(e.target.value)} />
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3 }}>Comma-separated (e.g. Atlanta, Dallas). Pair with the matching State above so a same-named city lands in the right state. Blank = no city filter.</div>
            </div>
            <div style={field}>
              <label style={label}>Age ranges ({ageRanges.length === 0 ? 'all ages' : `${ageRanges.length} selected`})</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                {AGE_BUCKETS.map((a) => (
                  <label key={a} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: '#e5e7eb', fontSize: 13 }}>
                    <input type="checkbox" checked={ageRanges.includes(a)} onChange={() => toggleAge(a)} />{a}
                  </label>
                ))}
              </div>
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3 }}>None checked = all ages.</div>
            </div>
            <div style={field}>
              <label style={label}>Gender ({gender.length === 0 ? 'any' : gender.join(', ')})</label>
              <div style={{ display: 'flex', gap: 14 }}>
                {GENDERS.map((g) => (
                  <label key={g.value} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: '#e5e7eb', fontSize: 13 }}>
                    <input type="checkbox" checked={gender.includes(g.value)} onChange={() => toggleGender(g.value)} />{g.label}
                  </label>
                ))}
              </div>
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3 }}>None checked = any gender.</div>
            </div>
            <div style={field}>
              <label style={label}>Income ({incomeRanges.length === 0 ? 'all' : `${incomeRanges.length} selected`})</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                {INCOME_RANGES.map((i) => (
                  <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: '#e5e7eb', fontSize: 12 }}>
                    <input type="checkbox" checked={incomeRanges.includes(i)} onChange={() => toggleIncome(i)} />{i}
                  </label>
                ))}
              </div>
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3 }}>None checked = all incomes. Exact vendor bands.</div>
            </div>
            <div style={field}>
              <label style={label}>Net worth ({netWorthRanges.length === 0 ? 'all' : `${netWorthRanges.length} selected`})</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                {NET_WORTH_RANGES.map((n) => (
                  <label key={n} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: '#e5e7eb', fontSize: 12 }}>
                    <input type="checkbox" checked={netWorthRanges.includes(n)} onChange={() => toggleNetWorth(n)} />{n}
                  </label>
                ))}
              </div>
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3 }}>None checked = all net worth. Exact vendor bands.</div>
            </div>
            <div style={field}>
              <label style={label}>Email validation ({emailValidation.length === 0 ? 'any' : emailValidation.join(', ')})</label>
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                {EMAIL_VALIDATION.map((v) => (
                  <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: '#e5e7eb', fontSize: 13 }}>
                    <input type="checkbox" checked={emailValidation.includes(v)} onChange={() => toggleEmailValidation(v)} />{v}
                  </label>
                ))}
              </div>
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3 }}>None checked = any. Matches personal_emails_validation_status.</div>
            </div>
          </>
        )}

        {!isIntent && (
          <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 8 }}>
            Mode-specific filters for <b>{spec.mode.kind}</b> jobs aren't editable in this form yet — those fields are preserved untouched on save. Common fields above still apply.
          </div>
        )}

        {destEditable && (
          <>
            <div style={sectionHead}>Destination</div>
            <DeliverToPicker kind={destKind} setKind={setDestKind} apiDestId={apiDestId} setApiDestId={setApiDestId} destinations={destinations} />
          </>
        )}
        {destEditable && destKind === 'data_body' && (
          <>
            {destinations.some((d) => d.kind === 'data_body') && (
              <div style={field}>
                <label style={label}>Use a saved destination</label>
                <select style={input} defaultValue="" onChange={(e) => { applyDestination(e.target.value); e.target.value = ''; }}>
                  <option value="" disabled>Pick a destination to fill the fields below…</option>
                  {destinations.filter((d) => d.kind === 'data_body').map((d) => (
                    <option key={d.id} value={d.id}>{d.name}{d.apiBaseUrl ? ` (${(() => { try { return new URL(d.apiBaseUrl!).host; } catch { return d.apiBaseUrl; } })()})` : ' (this env)'}</option>
                  ))}
                </select>
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3 }}>Picking one fills body UUID, API base URL, and login env-var names. Manage the list in the Destinations panel on the board.</div>
              </div>
            )}
            <div style={field}>
              <label style={label}>Destination body UUID</label>
              <input style={{ ...input, fontFamily: 'monospace' }} value={dataBodyId} onChange={(e) => setDataBodyId(e.target.value)} />
            </div>
            <div style={field}>
              <label style={label}>Target env API base URL</label>
              <input style={input} value={apiBaseUrl} placeholder="(local / this env)" onChange={(e) => setApiBaseUrl(e.target.value)} />
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3 }}>Set to https://my.databody.app to deliver into production.</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div style={field}>
                <label style={label}>Login email env-var name</label>
                <input style={{ ...input, fontFamily: 'monospace' }} value={loginEmailEnv} placeholder="e.g. RQ_PROD_IMPORT_EMAIL_ACME" onChange={(e) => setLoginEmailEnv(e.target.value)} />
              </div>
              <div style={field}>
                <label style={label}>Login password env-var name</label>
                <input style={{ ...input, fontFamily: 'monospace' }} value={loginPasswordEnv} placeholder="e.g. RQ_PROD_IMPORT_PASSWORD_ACME" onChange={(e) => setLoginPasswordEnv(e.target.value)} />
              </div>
            </div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: -4 }}>Env-var NAMES only — the actual credentials live in the droplet .env, never here. Required together for cross-env (prod) delivery.</div>
          </>
        )}

        {err && <div style={{ color: '#ef4444', fontSize: 13, margin: '12px 0' }}>{err}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Destination add/edit modal. Two kinds (migration 036):
//   Data Body  — import the pull into a Data Bodies body (production or this env). Only the
//                fields that delivery needs; env-var NAMES only, never secrets.
//   Custom API — the pull pushes each lead straight to an outside endpoint. Same settings as the
//                Custom API leg (mapping, static values, rate, headers, success condition).
//                No data body is created or used (customApiDelivery.ts on the backend).
// ============================================================
const PROD_API = 'https://my.databody.app';
const PROD_LOGIN_EMAIL_ENV = 'RQ_PROD_IMPORT_EMAIL';
const PROD_LOGIN_PASSWORD_ENV = 'RQ_PROD_IMPORT_PASSWORD';
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const onProdHost = () => { try { return window.location.host === new URL(PROD_API).host; } catch { return false; } };
const hint: React.CSSProperties = { fontSize: 11, color: '#6b7280', marginTop: 3 };
const req = <span style={{ color: '#f87171' }}> *</span>;
const miniBtn: React.CSSProperties = { background: 'none', border: '1px solid #2a2f3a', color: '#9ca3af', borderRadius: 6, cursor: 'pointer', fontSize: 12, padding: '4px 8px' };

type EnvChoice = 'production' | 'local' | 'other';
type MapRow = { key: string; dest: string; lead: string; required: boolean };
type KvRow = { key: string; name: string; value: string };
let rowSeq = 0;
const rk = () => `r${++rowSeq}`;

function DestinationModal({ existing, onClose, onSave }: {
  existing: AutomationDestination | null;   // null = add
  onClose: () => void;
  onSave: (id: string | null, input: AnyDestinationInput) => Promise<void>;
}) {
  const isEdit = !!existing;
  const [kind, setKind] = useState<DestinationKind>(existing?.kind ?? 'data_body');
  const [name, setName] = useState(existing?.name ?? '');
  const [notes, setNotes] = useState(existing?.notes ?? '');

  // ---- Data Body ----
  const initialEnv: EnvChoice = existing
    ? (!existing.apiBaseUrl ? 'local' : existing.apiBaseUrl.replace(/\/+$/, '') === PROD_API ? 'production' : 'other')
    : (onProdHost() ? 'local' : 'production');
  const [env, setEnv] = useState<EnvChoice>(initialEnv);
  const [otherUrl, setOtherUrl] = useState(initialEnv === 'other' ? (existing?.apiBaseUrl ?? '') : '');
  const [dataBodyId, setDataBodyId] = useState(existing?.kind === 'data_body' ? (existing.dataBodyId ?? '') : '');
  const [loginEmailEnv, setLoginEmailEnv] = useState(existing?.loginEmailEnv || PROD_LOGIN_EMAIL_ENV);
  const [loginPasswordEnv, setLoginPasswordEnv] = useState(existing?.loginPasswordEnv || PROD_LOGIN_PASSWORD_ENV);
  const [showLogin, setShowLogin] = useState(false);

  // ---- Custom API ---- (initialised from the saved config when editing)
  const ac = existing?.kind === 'custom_api' ? existing.apiConfig : null;
  const [url, setUrl] = useState(ac?.webhookUrl ?? '');
  const [method, setMethod] = useState<'POST' | 'PUT' | 'GET'>(ac?.webhookMethod ?? 'POST');
  const [bodyFormat, setBodyFormat] = useState<'json' | 'form'>(ac?.bodyFormat ?? 'json');
  const [mapRows, setMapRows] = useState<MapRow[]>(() => {
    const reqd = new Set(ac?.requiredFields ?? []);
    const rows = Object.entries(ac?.fieldMapping ?? {}).map(([dest, lead]) => ({ key: rk(), dest, lead, required: reqd.has(dest) }));
    return rows.length ? rows : [{ key: rk(), dest: '', lead: '', required: false }];
  });
  const [staticRows, setStaticRows] = useState<KvRow[]>(() => Object.entries(ac?.staticValues ?? {}).map(([n, v]) => ({ key: rk(), name: n, value: v })));
  const [rateMax, setRateMax] = useState(ac ? (ac.sendRateMax != null ? String(ac.sendRateMax) : '') : '5');
  const [rateWindow, setRateWindow] = useState(ac ? (ac.sendRateWindowSeconds != null ? String(ac.sendRateWindowSeconds) : '') : '1');
  const [headersRaw, setHeadersRaw] = useState(ac?.webhookHeaders && Object.keys(ac.webhookHeaders).length ? JSON.stringify(ac.webhookHeaders, null, 2) : '');
  const [useSuccess, setUseSuccess] = useState(!!ac?.successCondition);
  const [succField, setSuccField] = useState(ac?.successCondition?.field ?? '');
  const [succOp, setSuccOp] = useState<'equals' | 'not_equals' | 'contains' | 'exists' | 'regex'>(ac?.successCondition?.operator ?? 'equals');
  const [succValue, setSuccValue] = useState(ac?.successCondition?.value ?? '');

  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const buildDataBody = (): DestinationInput => {
    const apiBaseUrl = env === 'production' ? PROD_API : env === 'other' ? otherUrl.trim() : '';
    if (!name.trim()) {throw new Error('Name is required');}
    if (!UUID_RE.test(dataBodyId.trim())) {throw new Error('Destination body ID must be a UUID (copy it from the body\'s URL: /body/<id>)');}
    if (env === 'other' && !/^https?:\/\//i.test(apiBaseUrl)) {throw new Error('Environment URL must start with https://');}
    const remote = env !== 'local';
    if (remote && (!loginEmailEnv.trim() || !loginPasswordEnv.trim())) {throw new Error('A login (both env-var names) is required for another environment');}
    return {
      kind: 'data_body',
      name: name.trim(),
      apiBaseUrl: apiBaseUrl || null,
      dataBodyId: dataBodyId.trim(),
      loginEmailEnv: remote ? loginEmailEnv.trim() : null,
      loginPasswordEnv: remote ? loginPasswordEnv.trim() : null,
      notes: notes.trim() || null,
    };
  };

  const buildCustom = (): CustomApiDestinationInput => {
    if (!name.trim()) {throw new Error('Name is required');}
    if (!/^https?:\/\/\S+$/i.test(url.trim())) {throw new Error('URL must start with http(s)://');}
    const rows = mapRows.filter((r) => r.dest.trim() || r.lead.trim());
    if (!rows.length) {throw new Error('Map at least one field');}
    if (rows.some((r) => !r.dest.trim() || !r.lead.trim())) {throw new Error('Each mapping row needs both a destination field and a lead field');}
    const fieldMapping: Record<string, string> = {};
    rows.forEach((r) => { fieldMapping[r.dest.trim()] = r.lead.trim(); });
    const staticValues: Record<string, string> = {};
    staticRows.filter((r) => r.name.trim()).forEach((r) => { staticValues[r.name.trim()] = r.value; });
    let webhookHeaders: Record<string, string> | undefined;
    if (headersRaw.trim()) {
      try {
        const parsed = JSON.parse(headersRaw);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {throw new Error('not an object');}
        webhookHeaders = Object.fromEntries(Object.entries(parsed).map(([k, v]) => [k, String(v)]));
      } catch { throw new Error('Custom headers must be a JSON object, e.g. {"Authorization": "Bearer abc"}'); }
    }
    const intOrUndef = (s: string) => { const n = parseInt(s, 10); return Number.isFinite(n) && n > 0 ? n : undefined; };
    if (useSuccess && !succField.trim()) {throw new Error('Success condition needs a response field');}
    return {
      kind: 'custom_api',
      name: name.trim(),
      notes: notes.trim() || null,
      apiConfig: {
        webhookUrl: url.trim(),
        webhookMethod: method,
        bodyFormat,
        webhookHeaders,
        fieldMapping,
        requiredFields: rows.filter((r) => r.required).map((r) => r.dest.trim()),
        staticValues: Object.keys(staticValues).length ? staticValues : undefined,
        sendRateMax: intOrUndef(rateMax),
        sendRateWindowSeconds: intOrUndef(rateWindow),
        successCondition: useSuccess ? { field: succField.trim(), operator: succOp, value: succValue } : undefined,
      },
    };
  };

  const handleSave = async () => {
    setErr('');
    let payload: AnyDestinationInput;
    try { payload = kind === 'custom_api' ? buildCustom() : buildDataBody(); } catch (e: any) { setErr(e.message); return; }
    setSaving(true);
    try {
      await onSave(existing?.id ?? null, payload);
      onClose();
    } catch (e: any) {
      setErr(e?.response?.data?.error || e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const setMap = (key: string, patch: Partial<MapRow>) => setMapRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const setKv = (key: string, patch: Partial<KvRow>) => setStaticRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const thisHost = typeof window !== 'undefined' ? window.location.host : 'this env';

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1100, overflowY: 'auto', padding: '5vh 16px' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: kind === 'custom_api' ? 660 : 520, maxWidth: '100%', background: 'var(--card-bg, #1a1d24)', border: '1px solid #2a2f3a', borderRadius: 12, padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 14 }}>
          <h2 style={{ margin: 0, fontSize: 17, color: '#e5e7eb' }}>{isEdit ? 'Edit destination' : 'Add destination'}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        <div style={field}>
          <label style={label}>Destination type{req}</label>
          <select style={input} value={kind} disabled={isEdit} onChange={(e) => { setKind(e.target.value as DestinationKind); setErr(''); }}>
            <option value="data_body">Data Body: import leads into a Data Bodies body</option>
            <option value="custom_api">Custom API: push each lead to an outside endpoint</option>
          </select>
          {isEdit && <div style={hint}>Type can't be changed. Add a new destination instead.</div>}
        </div>

        <div style={field}>
          <label style={label}>Name{req}</label>
          <input style={input} value={name} placeholder={kind === 'custom_api' ? 'Acme CRM (API)' : 'Felerona FoxNews (prod)'} onChange={(e) => setName(e.target.value)} />
        </div>

        {kind === 'data_body' && (
          <>
            <div style={field}>
              <label style={label}>Environment{req}</label>
              <select style={input} value={env} onChange={(e) => setEnv(e.target.value as EnvChoice)}>
                {!onProdHost() && <option value="production">Production (my.databody.app)</option>}
                <option value="local">This environment ({thisHost})</option>
                <option value="other">Other URL…</option>
              </select>
              {env === 'other' && (
                <input style={{ ...input, marginTop: 8 }} value={otherUrl} placeholder="https://…" onChange={(e) => setOtherUrl(e.target.value)} />
              )}
            </div>
            <div style={field}>
              <label style={label}>Destination body ID{req}</label>
              <input style={{ ...input, fontFamily: 'monospace' }} value={dataBodyId} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" onChange={(e) => setDataBodyId(e.target.value)} />
              <div style={hint}>Open the body in {env === 'local' ? 'this environment' : env === 'production' ? 'production' : 'that environment'} and copy the ID from its URL (/body/&lt;id&gt;).</div>
            </div>
            {env !== 'local' && (
              <div style={{ ...field, background: '#12151c', border: '1px solid #23272f', borderRadius: 8, padding: '10px 12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <div style={{ fontSize: 12, color: '#cbd5e1' }}>
                    Login: <span style={{ fontFamily: 'monospace', color: '#9ca3af' }}>{loginEmailEnv || '—'} / {loginPasswordEnv || '—'}</span>
                  </div>
                  <button type="button" style={miniBtn} onClick={() => setShowLogin((s) => !s)}>{showLogin ? 'Done' : 'Change'}</button>
                </div>
                {showLogin && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
                    <input style={{ ...input, fontFamily: 'monospace' }} value={loginEmailEnv} placeholder="email env-var name" onChange={(e) => setLoginEmailEnv(e.target.value)} />
                    <input style={{ ...input, fontFamily: 'monospace' }} value={loginPasswordEnv} placeholder="password env-var name" onChange={(e) => setLoginPasswordEnv(e.target.value)} />
                  </div>
                )}
                <div style={hint}>Names of the droplet .env vars holding a login for that environment. Credentials never leave the server.</div>
              </div>
            )}
          </>
        )}

        {kind === 'custom_api' && (
          <>
              <div style={sectionHead}>Endpoint</div>
              <div style={field}>
                <label style={label}>URL{req}</label>
                <input style={input} value={url} placeholder="https://api.example.com/leads" onChange={(e) => setUrl(e.target.value)} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div style={field}>
                  <label style={label}>Method</label>
                  <select style={input} value={method} onChange={(e) => setMethod(e.target.value as 'POST' | 'PUT' | 'GET')}>
                    <option value="POST">POST</option><option value="PUT">PUT</option><option value="GET">GET</option>
                  </select>
                </div>
                <div style={field}>
                  <label style={label}>Body format</label>
                  <select style={input} value={bodyFormat} disabled={method === 'GET'} onChange={(e) => setBodyFormat(e.target.value as 'json' | 'form')}>
                    <option value="json">JSON</option><option value="form">Form (x-www-form-urlencoded)</option>
                  </select>
                  {method === 'GET' && <div style={hint}>GET sends fields in the query string.</div>}
                </div>
              </div>

              <div style={sectionHead}>Field mapping</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 64px 28px', gap: 6, alignItems: 'center', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.4, color: '#6b7280', marginBottom: 4 }}>
                <span>Destination field</span><span>Lead field</span><span style={{ textAlign: 'center' }}>Required</span><span />
              </div>
              {mapRows.map((r) => (
                <div key={r.key} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 64px 28px', gap: 6, alignItems: 'center', marginBottom: 6 }}>
                  <input style={input} value={r.dest} placeholder="e.g. email_address" onChange={(e) => setMap(r.key, { dest: e.target.value })} />
                  <input style={input} value={r.lead} placeholder="e.g. email" onChange={(e) => setMap(r.key, { lead: e.target.value })} />
                  <button type="button" onClick={() => setMap(r.key, { required: !r.required })} title="Required: if the lead has no value, it isn't sent and is marked failed"
                    style={{ ...miniBtn, padding: '6px 0', fontWeight: 700, color: r.required ? '#fff' : '#9ca3af', background: r.required ? '#b45309' : 'none', borderColor: r.required ? '#b45309' : '#2a2f3a' }}>
                    {r.required ? 'R' : 'O'}
                  </button>
                  <button type="button" onClick={() => setMapRows((rs) => (rs.length > 1 ? rs.filter((x) => x.key !== r.key) : [{ key: rk(), dest: '', lead: '', required: false }]))} style={{ ...miniBtn, padding: '6px 0' }} title="Remove">×</button>
                </div>
              ))}
              <button type="button" style={{ ...miniBtn, marginBottom: 6 }} onClick={() => setMapRows((rs) => [...rs, { key: rk(), dest: '', lead: '', required: false }])}>+ Add field</button>
              <div style={hint}>Lead field = the column name exactly as the pull delivers it (e.g. first_name, email, mobile_phone). Use dots in a destination field to nest it (e.g. contact.email).</div>

              <div style={sectionHead}>Static values <span style={{ fontWeight: 400, textTransform: 'none', color: '#6b7280' }}>(optional, sent on every lead)</span></div>
              {staticRows.map((r) => (
                <div key={r.key} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 28px', gap: 6, alignItems: 'center', marginBottom: 6 }}>
                  <input style={input} value={r.name} placeholder="field, e.g. list_id" onChange={(e) => setKv(r.key, { name: e.target.value })} />
                  <input style={input} value={r.value} placeholder="value" onChange={(e) => setKv(r.key, { value: e.target.value })} />
                  <button type="button" onClick={() => setStaticRows((rs) => rs.filter((x) => x.key !== r.key))} style={{ ...miniBtn, padding: '6px 0' }} title="Remove">×</button>
                </div>
              ))}
              <button type="button" style={miniBtn} onClick={() => setStaticRows((rs) => [...rs, { key: rk(), name: '', value: '' }])}>+ Add static value</button>

              <div style={sectionHead}>Delivery</div>
              <div style={field}>
                <label style={label}>Send rate</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#9ca3af', flexWrap: 'wrap' }}>
                  Send up to
                  <input style={{ ...input, width: 80 }} type="number" min={1} value={rateMax} placeholder="5" onChange={(e) => setRateMax(e.target.value)} />
                  {rateMax === '1' ? 'lead' : 'leads'} every
                  <input style={{ ...input, width: 80 }} type="number" min={1} value={rateWindow} placeholder="1" onChange={(e) => setRateWindow(e.target.value)} />
                  {rateWindow === '1' ? 'second' : 'seconds'}
                </div>
                <div style={hint}>The pull sends at this pace and retries errors (up to 4 tries). A slow rate on a big pull means the job runs longer before the next job starts.</div>
              </div>
              <div style={field}>
                <label style={label}>Custom headers (JSON, optional)</label>
                <textarea style={{ ...input, fontFamily: 'monospace', minHeight: 70, resize: 'vertical' }} value={headersRaw} placeholder='{"Authorization": "Bearer abc123"}' onChange={(e) => setHeadersRaw(e.target.value)} />
              </div>
              <div style={field}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: '#e5e7eb', fontSize: 13 }}>
                  <input type="checkbox" checked={useSuccess} onChange={(e) => setUseSuccess(e.target.checked)} />
                  Success condition <span style={{ color: '#6b7280', fontSize: 12 }}>(default: any 2xx response = delivered)</span>
                </label>
                {useSuccess && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px 1fr', gap: 6, marginTop: 8 }}>
                    <input style={input} value={succField} placeholder="response field, e.g. status" onChange={(e) => setSuccField(e.target.value)} />
                    <select style={input} value={succOp} onChange={(e) => setSuccOp(e.target.value as typeof succOp)}>
                      <option value="equals">equals</option><option value="not_equals">not equals</option><option value="contains">contains</option>
                      <option value="exists">exists</option><option value="regex">regex</option>
                    </select>
                    <input style={input} value={succValue} disabled={succOp === 'exists'} placeholder="value, e.g. accepted" onChange={(e) => setSuccValue(e.target.value)} />
                  </div>
                )}
              </div>

              <div style={{ fontSize: 12, color: '#9ca3af', background: '#12151c', border: '1px solid #23272f', borderRadius: 8, padding: '10px 12px', marginBottom: 14 }}>
                Jobs that pick this destination send each lead straight to this endpoint. Nothing is stored in a data body. Results show in the job's run log.
              </div>
            </>
        )}

        <div style={field}>
          <label style={label}>Notes (optional)</label>
          <input style={input} value={notes} placeholder="which account the creds belong to, etc." onChange={(e) => setNotes(e.target.value)} />
        </div>

        {err && <div style={{ color: '#ef4444', fontSize: 13, margin: '12px 0' }}>{err}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={() => { void handleSave(); }} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Destinations panel — collapsible registry manager folded into the automations page.
// ============================================================
function DestinationsPanel({ destinations, onSave, onDelete }: {
  destinations: AutomationDestination[];
  onSave: (id: string | null, input: AnyDestinationInput) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<AutomationDestination | null | 'new'>(null);
  const [err, setErr] = useState('');

  const host = (u: string | null) => { if (!u) {return 'this env';} try { return new URL(u).host; } catch { return u; } };
  const del = async (d: AutomationDestination) => {
    const msg = d.kind === 'custom_api'
      ? `Remove Custom API destination "${d.name}"? (Blocked if any job still uses it.)`
      : `Remove destination "${d.name}"? Jobs already using it keep working (values are copied into each job).`;
    if (!window.confirm(msg)) {return;}
    setErr('');
    try { await onDelete(d.id); } catch (e: any) { setErr(e?.response?.data?.error || e?.message || 'Delete failed'); }
  };

  return (
    <div style={{ marginTop: 16, border: '1px solid var(--border, #2a2f3a)', borderRadius: 10, background: 'var(--card-bg, #1a1d24)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', cursor: 'pointer', gap: 12 }} onClick={() => setOpen((o) => !o)}>
        <div style={{ fontWeight: 700, color: '#e5e7eb', fontSize: 14 }}>
          {open ? '▾' : '▸'} Destinations registry <span style={{ color: '#9ca3af', fontWeight: 400 }}>({destinations.length})</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 12, color: '#9ca3af' }}>reusable delivery targets, pick one when editing a job</span>
          <button className="btn btn-primary" style={{ fontSize: 12, padding: '4px 12px' }} onClick={(e) => { e.stopPropagation(); setEditing('new'); }}>+ Add destination</button>
        </div>
      </div>
      {open && (
        <div style={{ padding: '0 16px 16px' }}>
          {err && <div style={{ color: '#ef4444', fontSize: 13, marginBottom: 8 }}>{err}</div>}
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
            <thead>
              <tr>
                {['Name', 'Type', 'Where', 'Body / endpoint', 'Login env vars', 'Notes', ''].map((h) => (
                  <th key={h} style={{ ...th, position: 'static' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {destinations.length === 0 && (
                <tr><td style={td} colSpan={7}><span style={{ color: '#9ca3af' }}>No destinations yet. Add one to reuse it across jobs.</span></td></tr>
              )}
              {destinations.map((d) => (
                <tr key={d.id}>
                  <td style={{ ...td, fontWeight: 600 }}>{d.name}</td>
                  <td style={td}>
                    <Badge color={d.kind === 'custom_api' ? '#a78bfa' : '#60a5fa'}>{d.kind === 'custom_api' ? 'Custom API' : 'Data Body'}</Badge>
                  </td>
                  <td style={td}>{d.kind === 'custom_api' ? host(d.apiConfig?.webhookUrl ?? null) : host(d.apiBaseUrl)}</td>
                  <td style={{ ...td, fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-all' }}>
                    {d.kind === 'custom_api' ? `${d.apiConfig?.webhookMethod ?? 'POST'} ${d.apiConfig?.webhookUrl ?? ''}` : d.dataBodyId}
                  </td>
                  <td style={{ ...td, fontFamily: 'monospace', fontSize: 11, color: '#9ca3af' }}>
                    {d.loginEmailEnv ? <div>{d.loginEmailEnv}</div> : <span>—</span>}
                    {d.loginPasswordEnv && <div>{d.loginPasswordEnv}</div>}
                  </td>
                  <td style={{ ...td, color: '#9ca3af' }}>{d.notes || '—'}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap', textAlign: 'right' }}>
                    <button className="btn btn-secondary" style={{ fontSize: 12, padding: '3px 10px', marginRight: 6 }} onClick={() => setEditing(d)}>Edit</button>
                    <button className="btn btn-secondary" style={{ fontSize: 12, padding: '3px 10px', color: '#ef4444' }} onClick={() => { void del(d); }}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && (
        <DestinationModal
          existing={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSave={onSave}
        />
      )}
    </div>
  );
}

// ============================================================
// Add-Automation modal — builds a complete, valid intent → data_body_import spec from scratch
// (the customer-job pattern; other modes remain code-seeded). New jobs default to DISABLED so
// nothing fires before a test run. POSTs to createAutomation; 409 on a duplicate name.
// ============================================================
function AddAutomationModal({ clients, destinations, searchTopics, onClose, onCreate }: {
  clients: string[];
  destinations: AutomationDestination[];
  searchTopics: (q: string) => Promise<TopicHit[]>;
  onClose: () => void;
  onCreate: (spec: JobSpecLite) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [client, setClient] = useState('');
  const [schedule, setSchedule] = useState('8 AM PT');
  const [freshnessDays, setFreshnessDays] = useState('0');
  const [dailyBudget, setDailyBudget] = useState('0');

  const [topics, setTopics] = useState<TopicHit[]>([]);
  const [score, setScore] = useState<string[]>(['high']);
  const [lookbackDays, setLookbackDays] = useState('1');
  const [requireEmail, setRequireEmail] = useState(true);
  const [requireMobile, setRequireMobile] = useState(false);
  const [emailLastSeen, setEmailLastSeen] = useState('');
  const [states, setStates] = useState<string[]>([]);
  const [cities, setCities] = useState('');
  const [ageRanges, setAgeRanges] = useState<string[]>([]);
  const [gender, setGender] = useState<string[]>([]);
  const [incomeRanges, setIncomeRanges] = useState<string[]>([]);
  const [netWorthRanges, setNetWorthRanges] = useState<string[]>([]);
  const [emailValidation, setEmailValidation] = useState<string[]>([]);
  const [frequency, setFrequency] = useState<'daily' | 'weekly'>('daily');

  const [destKind, setDestKind] = useState<JobDestKind>('data_body');
  const [apiDestId, setApiDestId] = useState('');
  const [dataBodyId, setDataBodyId] = useState('');
  const [apiBaseUrl, setApiBaseUrl] = useState('');
  const [loginEmailEnv, setLoginEmailEnv] = useState('');
  const [loginPasswordEnv, setLoginPasswordEnv] = useState('');

  // Automation type: 'intent' = RetargetIQ audience (runs via @customers daily/weekly);
  // 'feed' = BigSky/DataMoon vendor feed (runs via the hourly @due cron by runAt).
  const [autoType, setAutoType] = useState<'intent' | 'feed'>('intent');
  // Intent-only extras (defaults match the Mainline convention).
  const [seedGraph, setSeedGraph] = useState(true);
  const [dedupeBy, setDedupeBy] = useState<'personal_emails' | 'mobile_phone' | ''>('personal_emails');
  // Feed-only fields.
  const [feedSource, setFeedSource] = useState<'bigsky' | 'datamoon'>('bigsky');
  const [feedId, setFeedId] = useState('');
  const [feedName, setFeedName] = useState('');
  const [feedDomain, setFeedDomain] = useState('');
  const [runAt, setRunAt] = useState('05:00');

  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const toggleScore = (s: string) => setScore((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));
  const toggleState = (s: string) => setStates((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));
  const toggleAge = (a: string) => setAgeRanges((cur) => (cur.includes(a) ? cur.filter((x) => x !== a) : [...cur, a]));
  const toggleGender = (g: string) => setGender((cur) => (cur.includes(g) ? cur.filter((x) => x !== g) : [...cur, g]));
  const toggleIncome = (i: string) => setIncomeRanges((cur) => (cur.includes(i) ? cur.filter((x) => x !== i) : [...cur, i]));
  const toggleNetWorth = (n: string) => setNetWorthRanges((cur) => (cur.includes(n) ? cur.filter((x) => x !== n) : [...cur, n]));
  const toggleEmailValidation = (v: string) => setEmailValidation((cur) => (cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]));
  const applyDestination = (id: string) => {
    const d = destinations.find((x) => x.id === id);
    if (!d) {return;}
    setDataBodyId(d.dataBodyId ?? '');
    setApiBaseUrl(d.apiBaseUrl ?? '');
    setLoginEmailEnv(d.loginEmailEnv ?? '');
    setLoginPasswordEnv(d.loginPasswordEnv ?? '');
  };

  const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const slugify = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40);

  const handleSave = async () => {
    setErr('');
    // --- shared: destination (both intent and feed deliver into a data body / custom API) ---
    if (destKind === 'custom_api' && !apiDestId) {return setErr('Pick a Custom API destination.');}
    if (destKind === 'data_body' && !dataBodyId.trim()) {return setErr('Destination body UUID is required.');}
    // Delivery host, if set, must be a bare origin (cross-env delivery). Leaving it blank delivers
    // in THIS environment (the normal case). Reject a full body URL like https://…/body/<uuid>.
    if (destKind === 'data_body' && apiBaseUrl.trim()) {
      let okUrl = false;
      try { const u = new URL(apiBaseUrl.trim()); okUrl = (u.pathname === '/' || u.pathname === '') && !u.search; } catch { okUrl = false; }
      if (!okUrl) {return setErr('Delivery host must be just an origin like https://my.databody.app (no /body/… path). Leave it blank to deliver in this environment.');}
    }
    const destination: JobSpecLite['destination'] = destKind === 'custom_api'
      ? { type: 'custom_api', destinationId: apiDestId }
      : {
        type: 'data_body_import',
        dataBodyId: dataBodyId.trim(),
        ...(apiBaseUrl.trim() ? { apiBaseUrl: apiBaseUrl.trim() } : {}),
        ...(loginEmailEnv.trim() ? { loginEmailEnv: loginEmailEnv.trim() } : {}),
        ...(loginPasswordEnv.trim() ? { loginPasswordEnv: loginPasswordEnv.trim() } : {}),
      };

    let spec: JobSpecLite;
    if (autoType === 'feed') {
      // Vendor feed (BigSky / DataMoon). Named feed_<slug>_<id8>; picked up by the hourly @due cron
      // at its run time (no @customers, no rq_cust_ prefix).
      if (!UUID_RE.test(feedId.trim())) {return setErr('Feed ID must be the vendor feed UUID.');}
      if (!feedName.trim()) {return setErr('Feed name is required (stamped on every lead as source_feed).');}
      if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(runAt.trim())) {return setErr("Run time must be 'HH:MM' 24h (e.g. 05:00).");}
      const lb = toNum(lookbackDays, 1);
      if (lb < 1 || lb > 7) {return setErr('Lookback must be 1–7 days for a feed.');}
      const jobName = (name.trim() || `feed_${slugify(feedName) || 'intent'}_${feedId.trim().slice(0, 8).toLowerCase()}`);
      if (!/^[a-z0-9_]+$/.test(jobName)) {return setErr('Job name must be lowercase letters, numbers, underscores.');}
      spec = {
        name: jobName,
        enabled: false,                 // starts off until a test run
        mode: {
          kind: 'datamoon_feed',
          source: feedSource,
          feedId: feedId.trim(),
          feedName: feedName.trim(),
          ...(feedDomain.trim() ? { domain: feedDomain.trim().toLowerCase() } : {}),
          lookbackDays: lb,
          requireEmail,
        },
        freshnessDays: 0,
        dailyBudget: toNum(dailyBudget, 0),
        allocation: 'none',
        frequency: 'daily',
        runAt: runAt.trim(),
        destination,
        ...(client.trim() ? { client: client.trim() } : {}),
        schedule: `daily ${runAt.trim()} PT (@due)`,
      };
    } else {
      // Intent audience (RetargetIQ). Name MUST be rq_cust_<client>_<code> so the nightly @customers
      // run picks it up (a name outside this convention is created but never run).
      if (!/^rq_cust_[a-z0-9]+_[a-z0-9_]+$/.test(name.trim())) {return setErr('Name must follow rq_cust_<client>_<code> (lowercase letters, numbers, underscores) so the nightly @customers run picks it up.');}
      if (topics.length === 0) {return setErr('Add at least one topic.');}
      if (score.length === 0) {return setErr('Select at least one score category.');}
      spec = {
        name: name.trim(),
        enabled: false,                 // safety: new jobs start off until a test run
        mode: {
          kind: 'intent',
          topicIds: topics.map((t) => t.id),
          topicLabels: topics.map((t) => t.label),
          scoreCategories: score,
          lookbackDays: toNum(lookbackDays, 1),
          requireEmail,
          requireMobile,
          ...(emailLastSeen.trim() ? { emailLastSeenDays: toNum(emailLastSeen) } : {}),
          ...(states.length ? { states } : {}),
          ...(cities.trim() ? { cities: cities.split(',').map((c) => c.trim()).filter(Boolean) } : {}),
          ...(ageRanges.length ? { ageRanges } : {}),
          ...(gender.length ? { gender } : {}),
          ...(incomeRanges.length ? { incomeRanges } : {}),
          ...(netWorthRanges.length ? { netWorthRanges } : {}),
          ...(emailValidation.length ? { emailValidation } : {}),
        },
        freshnessDays: toNum(freshnessDays, 0),
        dailyBudget: toNum(dailyBudget, 0),
        allocation: 'none',
        frequency,
        ...(seedGraph ? { seedGraph: true } : {}),
        ...(dedupeBy ? { dedupeInFlightBy: dedupeBy } : {}),
        destination,
        ...(client.trim() ? { client: client.trim() } : {}),
        ...(schedule.trim() ? { schedule: schedule.trim() } : {}),
      };
    }

    setSaving(true);
    try {
      await onCreate(spec);
      onClose();
    } catch (e: any) {
      setErr(e?.response?.data?.error || e?.message || 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, overflowY: 'auto', padding: '5vh 16px' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 560, maxWidth: '100%', background: 'var(--card-bg, #1a1d24)', border: '1px solid #2a2f3a', borderRadius: 12, padding: 24, boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
          <h2 style={{ margin: 0, fontSize: 18, color: '#e5e7eb' }}>New automation</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 10 }}>
          {autoType === 'intent'
            ? <>Creates a <b style={{ color: '#cbd5e1' }}>RetargetIQ intent</b> job (runs daily/weekly via @customers).</>
            : <>Creates a <b style={{ color: '#cbd5e1' }}>BigSky / DataMoon feed</b> job (runs hourly via @due at its run time).</>}
          {' '}Delivers to a data body. Starts <b style={{ color: '#cbd5e1' }}>disabled</b> — enable it after a test run.
        </div>

        <div style={sectionHead}>Automation type</div>
        <div style={{ display: 'flex', gap: 10, marginBottom: 4 }}>
          {([['intent', 'Intent audience (RetargetIQ)'], ['feed', 'Vendor feed (BigSky / DataMoon)']] as const).map(([val, lbl]) => (
            <button key={val} type="button" onClick={() => setAutoType(val)} style={{ flex: 1, padding: '8px 10px', fontSize: 13, borderRadius: 6, cursor: 'pointer', border: `1px solid ${autoType === val ? '#2563eb' : '#2a2f3a'}`, background: autoType === val ? '#1d4ed8' : '#0f1218', color: autoType === val ? '#fff' : '#9ca3af', fontWeight: autoType === val ? 700 : 400 }}>{lbl}</button>
          ))}
        </div>

        <div style={sectionHead}>Identity</div>
        <div style={field}>
          <label style={label}>Job name</label>
          <input style={{ ...input, fontFamily: 'monospace' }} value={name} placeholder={autoType === 'feed' ? '(auto: feed_<name>_<id8>)' : 'rq_cust_acme_topic_01'} onChange={(e) => setName(e.target.value)} />
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3 }}>{autoType === 'feed' ? 'Optional — leave blank to auto-name feed_<slug>_<id8>.' : 'Convention: rq_cust_<client>_<code>. Letters, numbers, underscores.'}</div>
        </div>
        <div style={field}>
          <label style={label}>Client</label>
          <input style={input} list="automation-clients-add" value={client} placeholder="(derive from name)" onChange={(e) => setClient(e.target.value)} />
          <datalist id="automation-clients-add">{clients.map((c) => <option key={c} value={c} />)}</datalist>
        </div>

        <div style={sectionHead}>Schedule & limits</div>
        {autoType === 'intent' ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12 }}>
            <div style={field}>
              <label style={label}>Frequency</label>
              <select style={input} value={frequency} onChange={(e) => setFrequency(e.target.value as 'daily' | 'weekly')}>
                <option value="daily">Daily (8 AM PT)</option>
                <option value="weekly">Weekly (Wed 8 AM PT)</option>
              </select>
            </div>
            <div style={field}><label style={label}>Schedule label</label><input style={input} value={schedule} onChange={(e) => setSchedule(e.target.value)} /></div>
            <div style={field}><label style={label}>Freshness (days)</label><input style={input} type="number" value={freshnessDays} onChange={(e) => setFreshnessDays(e.target.value)} /></div>
            <div style={field}><label style={label}>Daily cap</label><input style={input} type="number" value={dailyBudget} onChange={(e) => setDailyBudget(e.target.value)} /></div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div style={field}><label style={label}>Run time (HH:MM PT)</label><input style={input} value={runAt} placeholder="05:00" onChange={(e) => setRunAt(e.target.value)} /></div>
            <div style={field}><label style={label}>Lookback (days, 1–7)</label><input style={input} type="number" value={lookbackDays} onChange={(e) => setLookbackDays(e.target.value)} /></div>
            <div style={field}><label style={label}>Daily cap (0 = none)</label><input style={input} type="number" value={dailyBudget} onChange={(e) => setDailyBudget(e.target.value)} /></div>
          </div>
        )}

        {autoType === 'intent' && (<>
        <div style={sectionHead}>Topic & intent filters</div>
        <div style={field}>
          <label style={label}>Topics *</label>
          <TopicPicker value={topics} onChange={setTopics} search={searchTopics} />
        </div>
        <div style={field}>
          <label style={label}>Score categories *</label>
          <div style={{ display: 'flex', gap: 14 }}>
            {SCORE_CATEGORIES.map((s) => (
              <label key={s} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: '#e5e7eb', fontSize: 13 }}>
                <input type="checkbox" checked={score.includes(s)} onChange={() => toggleScore(s)} />{s}
              </label>
            ))}
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div style={field}><label style={label}>Intent lookback (days)</label><input style={input} type="number" value={lookbackDays} onChange={(e) => setLookbackDays(e.target.value)} /></div>
          <div style={field}><label style={label}>Email last-seen ≤ (days)</label><input style={input} type="number" value={emailLastSeen} placeholder="(none)" onChange={(e) => setEmailLastSeen(e.target.value)} /></div>
        </div>
        <div style={{ display: 'flex', gap: 20, ...field }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: '#e5e7eb', fontSize: 13 }}><input type="checkbox" checked={requireEmail} onChange={(e) => setRequireEmail(e.target.checked)} />require email</label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: '#e5e7eb', fontSize: 13 }}><input type="checkbox" checked={requireMobile} onChange={(e) => setRequireMobile(e.target.checked)} />require mobile</label>
        </div>
        <div style={field}>
          <label style={label}>States ({states.length === 0 ? 'all states' : `${states.length} selected`})</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(9, 1fr)', gap: 4, marginBottom: 6 }}>
            {US_STATES.map((s) => {
              const on = states.includes(s);
              return (
                <button key={s} type="button" onClick={() => toggleState(s)} style={{ padding: '4px 0', fontSize: 11, borderRadius: 4, cursor: 'pointer', border: `1px solid ${on ? '#2563eb' : '#2a2f3a'}`, background: on ? '#1d4ed8' : '#0f1218', color: on ? '#fff' : '#9ca3af', fontWeight: on ? 700 : 400 }}>{s}</button>
              );
            })}
          </div>
          <div style={{ display: 'flex', gap: 10, fontSize: 11 }}>
            <button type="button" onClick={() => setStates([])} style={{ background: 'none', border: 'none', color: '#60a5fa', cursor: 'pointer', padding: 0 }}>clear (= all)</button>
            <button type="button" onClick={() => setStates(US_STATES.filter((s) => s !== 'CA' && s !== 'DC'))} style={{ background: 'none', border: 'none', color: '#60a5fa', cursor: 'pointer', padding: 0 }}>49 (excl. CA, DC)</button>
          </div>
        </div>
        <div style={field}>
          <label style={label}>Cities</label>
          <input style={input} value={cities} placeholder="(no city filter)" onChange={(e) => setCities(e.target.value)} />
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3 }}>Comma-separated (e.g. Atlanta, Dallas). Pair with the matching State so a same-named city lands in the right state. Blank = no city filter.</div>
        </div>
        <div style={field}>
          <label style={label}>Age ranges ({ageRanges.length === 0 ? 'all ages' : `${ageRanges.length} selected`})</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            {AGE_BUCKETS.map((a) => (
              <label key={a} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: '#e5e7eb', fontSize: 13 }}>
                <input type="checkbox" checked={ageRanges.includes(a)} onChange={() => toggleAge(a)} />{a}
              </label>
            ))}
          </div>
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3 }}>None checked = all ages.</div>
        </div>
        <div style={field}>
          <label style={label}>Gender ({gender.length === 0 ? 'any' : gender.join(', ')})</label>
          <div style={{ display: 'flex', gap: 14 }}>
            {GENDERS.map((g) => (
              <label key={g.value} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: '#e5e7eb', fontSize: 13 }}>
                <input type="checkbox" checked={gender.includes(g.value)} onChange={() => toggleGender(g.value)} />{g.label}
              </label>
            ))}
          </div>
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3 }}>None checked = any gender.</div>
        </div>
        <div style={field}>
          <label style={label}>Income ({incomeRanges.length === 0 ? 'all' : `${incomeRanges.length} selected`})</label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
            {INCOME_RANGES.map((i) => (
              <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: '#e5e7eb', fontSize: 12 }}>
                <input type="checkbox" checked={incomeRanges.includes(i)} onChange={() => toggleIncome(i)} />{i}
              </label>
            ))}
          </div>
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3 }}>None checked = all incomes. Exact vendor bands.</div>
        </div>
        <div style={field}>
          <label style={label}>Net worth ({netWorthRanges.length === 0 ? 'all' : `${netWorthRanges.length} selected`})</label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
            {NET_WORTH_RANGES.map((n) => (
              <label key={n} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: '#e5e7eb', fontSize: 12 }}>
                <input type="checkbox" checked={netWorthRanges.includes(n)} onChange={() => toggleNetWorth(n)} />{n}
              </label>
            ))}
          </div>
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3 }}>None checked = all net worth. Exact vendor bands.</div>
        </div>
        <div style={field}>
          <label style={label}>Email validation ({emailValidation.length === 0 ? 'any' : emailValidation.join(', ')})</label>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            {EMAIL_VALIDATION.map((v) => (
              <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: '#e5e7eb', fontSize: 13 }}>
                <input type="checkbox" checked={emailValidation.includes(v)} onChange={() => toggleEmailValidation(v)} />{v}
              </label>
            ))}
          </div>
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3 }}>None checked = any. Matches personal_emails_validation_status.</div>
        </div>

        <div style={sectionHead}>Graph & dedupe</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div style={field}>
            <label style={label}>Seed identity graph</label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: '#e5e7eb', fontSize: 13, marginTop: 4 }}>
              <input type="checkbox" checked={seedGraph} onChange={(e) => setSeedGraph(e.target.checked)} />stage the full audience into the identity graph
            </label>
          </div>
          <div style={field}>
            <label style={label}>In-flight dedupe</label>
            <select style={input} value={dedupeBy} onChange={(e) => setDedupeBy(e.target.value as 'personal_emails' | 'mobile_phone' | '')}>
              <option value="personal_emails">personal_emails</option>
              <option value="mobile_phone">mobile_phone</option>
              <option value="">none</option>
            </select>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3 }}>Collapses duplicates within a single pull only.</div>
          </div>
        </div>
        </>)}

        {autoType === 'feed' && (<>
        <div style={sectionHead}>Feed source</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div style={field}>
            <label style={label}>Source</label>
            <select style={input} value={feedSource} onChange={(e) => setFeedSource(e.target.value as 'bigsky' | 'datamoon')}>
              <option value="bigsky">BigSky (intent.bigskydata.net)</option>
              <option value="datamoon">DataMoon</option>
            </select>
          </div>
          <div style={field}><label style={label}>Feed name *</label><input style={input} value={feedName} placeholder="e.g. Medicare-1" onChange={(e) => setFeedName(e.target.value)} /></div>
        </div>
        <div style={field}>
          <label style={label}>Feed ID (UUID) *</label>
          <input style={{ ...input, fontFamily: 'monospace' }} value={feedId} placeholder="b00d0d16-efe1-4075-85dd-ae988be14f1a" onChange={(e) => setFeedId(e.target.value)} />
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3 }}>Vendor feed UUID from the portal. The feed name is stamped on every lead as source_feed.</div>
        </div>
        <div style={field}>
          <label style={label}>Domain (optional)</label>
          <input style={input} value={feedDomain} placeholder="(only if the feed is locked to one site)" onChange={(e) => setFeedDomain(e.target.value)} />
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3 }}>Single-site feeds stamp this as source_domain. Leave blank otherwise.</div>
        </div>
        <div style={{ ...field, display: 'flex', gap: 20 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: '#e5e7eb', fontSize: 13 }}><input type="checkbox" checked={requireEmail} onChange={(e) => setRequireEmail(e.target.checked)} />require email (drop leads with no email)</label>
        </div>
        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>Feeds deliver to the body only — they do <b style={{ color: '#9ca3af' }}>not</b> seed the identity graph (their headers/values need mapping + sign-off first).</div>
        </>)}

        <div style={sectionHead}>Destination</div>
        <DeliverToPicker kind={destKind} setKind={setDestKind} apiDestId={apiDestId} setApiDestId={setApiDestId} destinations={destinations} />
        {destKind === 'data_body' && (<>
        {destinations.some((d) => d.kind === 'data_body') && (
          <div style={field}>
            <label style={label}>Use a saved destination</label>
            <select style={input} defaultValue="" onChange={(e) => { applyDestination(e.target.value); e.target.value = ''; }}>
              <option value="" disabled>Pick a destination to fill the fields below…</option>
              {destinations.filter((d) => d.kind === 'data_body').map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
        )}
        <div style={field}>
          <label style={label}>Destination body UUID *</label>
          <input style={{ ...input, fontFamily: 'monospace' }} value={dataBodyId} onChange={(e) => setDataBodyId(e.target.value)} />
        </div>
        <div style={field}>
          <label style={label}>Target env API base URL</label>
          <input style={input} value={apiBaseUrl} placeholder="(local / this env)" onChange={(e) => setApiBaseUrl(e.target.value)} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div style={field}><label style={label}>Login email env-var</label><input style={{ ...input, fontFamily: 'monospace' }} value={loginEmailEnv} placeholder="e.g. RQ_PROD_IMPORT_EMAIL_ACME" onChange={(e) => setLoginEmailEnv(e.target.value)} /></div>
          <div style={field}><label style={label}>Login password env-var</label><input style={{ ...input, fontFamily: 'monospace' }} value={loginPasswordEnv} placeholder="e.g. RQ_PROD_IMPORT_PASSWORD_ACME" onChange={(e) => setLoginPasswordEnv(e.target.value)} /></div>
        </div>
        </>)}

        {err && <div style={{ color: '#ef4444', fontSize: 13, margin: '12px 0' }}>{err}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Creating…' : 'Create (disabled)'}</button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Run modal — kicks off a --limit-rows=1 test run and streams the captured output by polling.
// ============================================================
function RunModal({ jobName, start, poll, onClose }: {
  jobName: string;
  start: (jobName: string) => Promise<string>;
  poll: (runId: string) => Promise<AutomationRun>;
  onClose: () => void;
}) {
  const [run, setRun] = useState<AutomationRun | null>(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    let cancelled = false;
    void (async () => {
      try {
        const runId = await start(jobName);
        const tick = async () => {
          if (cancelled) {return;}
          try {
            const r = await poll(runId);
            if (cancelled) {return;}
            setRun(r);
            if (r.status === 'running') {timer = setTimeout(tick, 2000);}
          } catch (e: any) {
            if (!cancelled) {setErr(e?.response?.data?.error || e?.message || 'Lost track of the run');}
          }
        };
        void tick();
      } catch (e: any) {
        if (!cancelled) {setErr(e?.response?.data?.error || e?.message || 'Failed to start run');}
      }
    })();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [start, poll, jobName]);

  const color = run?.status === 'done' ? '#22c55e' : run?.status === 'error' ? '#ef4444' : '#f59e0b';
  const statusText = !run ? 'starting…' : run.status === 'running' ? 'running…' : `${run.status}${run.exitCode != null ? ` (exit ${run.exitCode})` : ''}`;

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, overflowY: 'auto', padding: '5vh 16px' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 720, maxWidth: '100%', background: 'var(--card-bg, #1a1d24)', border: '1px solid #2a2f3a', borderRadius: 12, padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
          <h2 style={{ margin: 0, fontSize: 17, color: '#e5e7eb' }}>Test run <span style={{ fontFamily: 'monospace', fontSize: 13, color: '#9ca3af' }}>{jobName}</span></h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 10 }}>
          One lead per body (<span style={{ fontFamily: 'monospace' }}>--limit-rows=1</span>). This performs a real delivery of that lead. <Badge color={color}>{statusText}</Badge>
        </div>
        {err && <div style={{ color: '#ef4444', fontSize: 13, marginBottom: 8 }}>{err}</div>}
        <pre style={{
          margin: 0, padding: 12, background: '#0b0e13', border: '1px solid #23272f', borderRadius: 8,
          color: '#cbd5e1', fontSize: 12, lineHeight: 1.5, maxHeight: '55vh', overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        }}>
          {run?.output?.length ? run.output.join('\n') : 'waiting for output…'}
        </pre>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Board view model — search / status tabs / filters / grouping / sort / paging.
// Pure UI: everything is derived client-side from GET /api/admin/automations.
// The view (filters, sort, grouping, page size) is remembered per browser.
// ============================================================
type StatusTab = 'all' | 'error' | 'warn' | 'ok' | 'disabled';
type SortKey = 'status' | 'client' | 'name' | 'topic' | 'today' | 'last';
type SortDir = 'asc' | 'desc';
interface BoardView {
  q: string; tab: StatusTab; client: string; issue: string; freq: string; delivery: string;
  groupBy: 'none' | 'client'; sortKey: SortKey; sortDir: SortDir; pageSize: number;
}
const VIEW_KEY = 'automations-board-view-v1';
const DEFAULT_VIEW: BoardView = {
  q: '', tab: 'all', client: '', issue: '', freq: '', delivery: '',
  groupBy: 'none', sortKey: 'status', sortDir: 'asc', pageSize: 50,
};
function loadView(): BoardView {
  try {
    const raw = window.localStorage.getItem(VIEW_KEY);
    if (raw) { return { ...DEFAULT_VIEW, ...(JSON.parse(raw) as Partial<BoardView>) }; }
  } catch { /* storage unavailable — use defaults */ }
  return DEFAULT_VIEW;
}
function saveView(v: BoardView) {
  try { window.localStorage.setItem(VIEW_KEY, JSON.stringify(v)); } catch { /* ignore */ }
}

const HEALTH_RANK: Record<string, number> = { error: 0, warn: 1, ok: 2 };
const rowName = (r: AutomationRow) => r.dataBodyName || r.jobName;
const freqOf = (r: AutomationRow): 'daily' | 'weekly' => (r.spec?.frequency === 'weekly' ? 'weekly' : 'daily');
// Group flags that differ only by a number ("no delivery in 3d" / "… 5d") into one issue type.
const issueKey = (f: string) => f.replace(/\d[\d,.]*/g, '#').trim();

const SORTS: Record<SortKey, { label: string; get: (r: AutomationRow) => string | number }> = {
  status: { label: 'Status', get: (r) => (HEALTH_RANK[r.health] ?? 3) + (r.enabled ? 0 : 0.5) },
  client: { label: 'Client', get: (r) => (r.client || '').toLowerCase() },
  name: { label: 'Name', get: (r) => rowName(r).toLowerCase() },
  topic: { label: 'Topic', get: (r) => (r.topic || r.mode || '').toLowerCase() },
  today: { label: 'Leads today', get: (r) => (r.leadsToday == null ? -1 : r.leadsToday) },
  last: { label: 'Last delivery', get: (r) => (r.lastLeadReceived ? new Date(r.lastLeadReceived).getTime() : 0) },
};

function haystack(r: AutomationRow): string {
  return [
    r.jobName, r.client, r.dataBodyName, r.dataBodyId, r.topic, r.mode, r.states, r.schedule,
    r.destinationType, (r.topicIds || []).join(' '), r.flags.join(' '), r.enabled ? 'enabled' : 'disabled',
    HEALTH_LABEL[r.health],
  ].filter(Boolean).join(' ').toLowerCase();
}

// One-line filter summary for the collapsed row; the full breakdown lives in the details panel.
function filterSummary(r: AutomationRow): string {
  const parts: string[] = [];
  if (r.lookbackDays != null) { parts.push(r.lookbackDays === 1 ? 'today only' : `${r.lookbackDays}d lookback`); }
  if (r.score && r.score.length) { parts.push(`score ${r.score.join('/')}`); }
  const req = [r.requireEmail && 'email', r.requireMobile && 'mobile'].filter(Boolean).join('+');
  if (req) { parts.push(`req ${req}`); }
  parts.push(r.dailyCap ? `${r.dailyCap.toLocaleString()}/day` : 'no cap');
  parts.push(freqOf(r));
  return parts.join(' · ');
}

const C = {
  panel: 'var(--card-bg, #1a1d24)', line: '#262b36', sub: '#9ca3af', dim: '#6b7280', text: '#e5e7eb', accent: '#6366f1',
};
const ctrl: React.CSSProperties = {
  padding: '7px 10px', fontSize: 13, color: C.text, background: '#0f1218', border: '1px solid #2a2f3a',
  borderRadius: 8, height: 34, boxSizing: 'border-box',
};
const cell: React.CSSProperties = {
  padding: '10px 12px', fontSize: 13, color: C.text, borderBottom: `1px solid ${C.line}`, verticalAlign: 'middle',
  overflow: 'hidden',
};
const headCell: React.CSSProperties = {
  textAlign: 'left', padding: '9px 12px', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4,
  color: C.sub, borderBottom: '1px solid #2a2f3a', whiteSpace: 'nowrap', position: 'sticky', top: 0,
  background: '#151820', zIndex: 1, userSelect: 'none',
};
const ellipsis: React.CSSProperties = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
const linkBtn: React.CSSProperties = { background: 'none', border: 'none', color: '#818cf8', cursor: 'pointer', padding: 0, fontSize: 12 };

function StatusCard({ label: text, count, color, active, onClick }: {
  label: string; count: number; color: string; active: boolean; onClick: () => void;
}) {
  return (
    <button
      type="button" onClick={onClick}
      style={{
        flex: '1 1 120px', minWidth: 110, textAlign: 'left', cursor: 'pointer', borderRadius: 10, padding: '10px 14px',
        background: active ? `${color}1f` : '#12151c', border: `1px solid ${active ? color : '#2a2f3a'}`,
        boxShadow: active ? `inset 3px 0 0 ${color}` : `inset 3px 0 0 ${color}66`,
      }}
    >
      <div style={{ fontSize: 22, fontWeight: 700, color: count > 0 || active ? C.text : C.dim, lineHeight: 1.1 }}>{count}</div>
      <div style={{ fontSize: 12, color: active ? color : C.sub, fontWeight: 600, marginTop: 2 }}>{text}</div>
    </button>
  );
}

function DetailItem({ k, children }: { k: string; children: React.ReactNode }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.4, color: C.dim, marginBottom: 2 }}>{k}</div>
      <div style={{ fontSize: 12, color: '#cbd5e1', wordBreak: 'break-word' }}>{children}</div>
    </div>
  );
}

function RowDetails({ r }: { r: AutomationRow }) {
  const none = <span style={{ color: C.dim }}>—</span>;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px 20px', padding: '4px 4px 6px' }}>
      <DetailItem k="Job name"><span style={{ fontFamily: 'monospace' }}>{r.jobName}</span></DetailItem>
      <DetailItem k="Client">{r.client || none}</DetailItem>
      <DetailItem k="Destination">
        {r.dataBodyName || none}
        {r.dataBodyStatus && r.dataBodyStatus !== 'active' && r.dataBodyStatus !== 'custom_api' && r.dataBodyStatus !== 'external' && <span style={{ color: '#f59e0b' }}> ({r.dataBodyStatus})</span>}
        {r.dataBodyId && <div style={{ fontFamily: 'monospace', fontSize: 11, color: C.dim }}>{r.dataBodyId}</div>}
      </DetailItem>
      <DetailItem k="Destination type">{r.destinationType || none}</DetailItem>
      <DetailItem k="Mode">{r.mode || none}</DetailItem>
      <DetailItem k="Topic">{r.topic || none}{r.topicIds && r.topicIds.length > 0 && <div style={{ fontSize: 11, color: C.dim }}>#{r.topicIds.join(', ')}</div>}</DetailItem>
      <DetailItem k="Score">{r.score && r.score.length ? r.score.join(', ') : none}</DetailItem>
      <DetailItem k="Lookback">{r.lookbackDays == null ? none : r.lookbackDays === 1 ? 'Today' : `${r.lookbackDays}d`}</DetailItem>
      <DetailItem k="Requires">{[r.requireEmail && 'email', r.requireMobile && 'mobile'].filter(Boolean).join(' + ') || none}</DetailItem>
      <DetailItem k="Email seen ≤">{r.emailLastSeenDays != null ? `${r.emailLastSeenDays}d` : none}</DetailItem>
      <DetailItem k="Geo / demo filters">{r.states || none}</DetailItem>
      <DetailItem k="Dedupe">{r.dedupeBy || none}</DetailItem>
      <DetailItem k="Freshness">{r.freshnessDays}d</DetailItem>
      <DetailItem k="Daily cap">{r.dailyCap ? r.dailyCap.toLocaleString() : 'no cap'}</DetailItem>
      <DetailItem k="Schedule">{r.schedule || none} · {freqOf(r)}</DetailItem>
      <DetailItem k="Leads today / last delivery">{fmtNum(r.leadsToday)} · {fmtWhen(r.lastLeadReceived)}</DetailItem>
      {r.flags.length > 0 && (
        <div style={{ gridColumn: '1 / -1' }}>
          <DetailItem k="All flags">
            {r.flags.map((f) => <div key={f} style={{ color: r.health === 'error' ? '#f87171' : '#fbbf24' }}>• {f}</div>)}
          </DetailItem>
        </div>
      )}
    </div>
  );
}

function EnabledSwitch({ on, busy, onToggle }: { on: boolean; busy: boolean; onToggle: () => void }) {
  return (
    <button
      type="button" role="switch" aria-checked={on} disabled={busy} onClick={onToggle}
      title={on ? 'Enabled — click to disable (skipped by the next pull)' : 'Disabled — click to enable'}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', padding: 0, cursor: busy ? 'wait' : 'pointer', color: on ? '#4ade80' : C.dim, fontSize: 11, fontWeight: 600 }}
    >
      <span style={{ width: 26, height: 14, borderRadius: 999, background: on ? '#16a34a' : '#374151', position: 'relative', transition: 'background .15s', flexShrink: 0 }}>
        <span style={{ position: 'absolute', top: 2, left: on ? 14 : 2, width: 10, height: 10, borderRadius: '50%', background: '#fff', transition: 'left .15s' }} />
      </span>
      {busy ? '…' : on ? 'On' : 'Off'}
    </button>
  );
}

export default function Automations() {
  const { user, getToken } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  const [data, setData] = useState<AutomationsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<AutomationRow | null>(null);
  const [adding, setAdding] = useState(false);
  const [runningJob, setRunningJob] = useState<string | null>(null); // job with an open test-run modal
  const [busyName, setBusyName] = useState<string | null>(null); // job being toggled
  const [destinations, setDestinations] = useState<AutomationDestination[]>([]);

  // ---- board view state (remembered per browser) ----
  const [view, setView] = useState<BoardView>(loadView);
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => new Set());
  const searchRef = useRef<HTMLInputElement>(null);
  const patchView = useCallback((p: Partial<BoardView>) => {
    setView((v) => { const next = { ...v, ...p }; saveView(next); return next; });
    setPage(0);
  }, []);
  const resetView = () => { setView(DEFAULT_VIEW); saveView(DEFAULT_VIEW); setPage(0); };
  const viewIsDefault = (Object.keys(DEFAULT_VIEW) as (keyof BoardView)[])
    .filter((k) => k !== 'pageSize' && k !== 'groupBy' && k !== 'sortKey' && k !== 'sortDir')
    .every((k) => view[k] === DEFAULT_VIEW[k]);

  // "/" focuses search (when not typing elsewhere); Esc in the box clears it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      const typing = t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable);
      if (e.key === '/' && !typing) { e.preventDefault(); searchRef.current?.focus(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = await getToken();
      if (!token) { setError('Your session expired. Please sign in again.'); return; }
      const res = await getAutomations(token);
      setData(res);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load automations');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => { if (isSuperAdmin) {void load();} }, [isSuperAdmin, load]);

  // Destinations registry — loaded alongside the board; feeds the edit modal's picker + the panel.
  const loadDestinations = useCallback(async () => {
    const token = await getToken();
    if (!token) {return;}
    try { setDestinations(await listAutomationDestinations(token)); } catch { /* non-fatal for the board */ }
  }, [getToken]);
  useEffect(() => { if (isSuperAdmin) {void loadDestinations();} }, [isSuperAdmin, loadDestinations]);

  const saveDestination = useCallback(async (id: string | null, inputVal: AnyDestinationInput) => {
    const token = await getToken();
    if (!token) {throw new Error('Your session expired. Please sign in again.');}
    if (id) {await updateAutomationDestination(token, id, inputVal);} else {await createAutomationDestination(token, inputVal);}
    await loadDestinations();
  }, [getToken, loadDestinations]);

  const removeDestination = useCallback(async (id: string) => {
    const token = await getToken();
    if (!token) {throw new Error('Your session expired. Please sign in again.');}
    await deleteAutomationDestination(token, id);
    await loadDestinations();
  }, [getToken, loadDestinations]);

  const statsPending = !!data?.statsPending;
  useEffect(() => {
    if (!isSuperAdmin || !statsPending) {return;}
    const t = setTimeout(() => { void load(); }, 5000);
    return () => clearTimeout(t);
  }, [isSuperAdmin, statsPending, data?.generatedAt, load]);

  // Save an edited spec (throws on validation/API error so the modal can show it inline).
  const saveSpec = useCallback(async (spec: JobSpecLite) => {
    const token = await getToken();
    if (!token) {throw new Error('Your session expired. Please sign in again.');}
    await updateAutomation(token, spec.name, spec);
    await load();
  }, [getToken, load]);

  // Create a new job (throws so the add modal can show validation/409 inline).
  const createSpec = useCallback(async (spec: JobSpecLite) => {
    const token = await getToken();
    if (!token) {throw new Error('Your session expired. Please sign in again.');}
    await createAutomation(token, spec);
    await load();
  }, [getToken, load]);

  // Topic search wrapper — supplies the token so the modal/picker stay token-agnostic.
  const doTopicSearch = useCallback(async (q: string): Promise<TopicHit[]> => {
    const token = await getToken();
    if (!token) {throw new Error('Your session expired. Please sign in again.');}
    return searchAutomationTopics(token, q);
  }, [getToken]);

  // Test-run controls (stable so the run modal's effect runs once per open).
  const startRun = useCallback(async (name: string) => {
    const token = await getToken();
    if (!token) {throw new Error('Your session expired. Please sign in again.');}
    return startAutomationTestRun(token, name);
  }, [getToken]);
  const pollRun = useCallback(async (runId: string) => {
    const token = await getToken();
    if (!token) {throw new Error('Your session expired. Please sign in again.');}
    return getAutomationRun(token, runId);
  }, [getToken]);

  // Toggle the safety gate straight from the row (no modal).
  const toggleEnabled = useCallback(async (row: AutomationRow) => {
    setBusyName(row.jobName);
    setError('');
    try {
      const token = await getToken();
      if (!token) { setError('Your session expired. Please sign in again.'); return; }
      await setAutomationEnabled(token, row.jobName, !row.enabled);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to toggle job');
    } finally {
      setBusyName(null);
    }
  }, [getToken, load]);

  const rows: AutomationRow[] = useMemo(() => data?.automations ?? [], [data?.automations]);

  // Distinct client names for the edit modal's picker (existing clients, excluding Internal).
  const clientList = useMemo(
    () => Array.from(new Set(rows.map((r) => r.client).filter((c) => c && c !== 'Internal'))).sort(),
    [rows],
  );
  // Filter dropdown options (all clients incl. Internal, with counts).
  const clientOptions = useMemo(() => {
    const m = new Map<string, number>();
    rows.forEach((r) => m.set(r.client || '(none)', (m.get(r.client || '(none)') ?? 0) + 1));
    return Array.from(m.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [rows]);
  const issueOptions = useMemo(() => {
    const m = new Map<string, number>();
    rows.forEach((r) => new Set(r.flags.map(issueKey)).forEach((k) => m.set(k, (m.get(k) ?? 0) + 1)));
    return Array.from(m.entries()).sort((a, b) => b[1] - a[1]);
  }, [rows]);

  // Everything except the status tab — so the status cards show counts within the current search/filters.
  const baseFiltered = useMemo(() => {
    const terms = view.q.toLowerCase().split(/\s+/).filter(Boolean);
    return rows.filter((r) => {
      if (view.client && (r.client || '(none)') !== view.client) {return false;}
      if (view.issue && !r.flags.some((f) => issueKey(f) === view.issue)) {return false;}
      if (view.freq && freqOf(r) !== view.freq) {return false;}
      if (view.delivery === 'today' && !((r.leadsToday ?? 0) > 0)) {return false;}
      if (view.delivery === 'none' && (r.leadsToday ?? 0) > 0) {return false;}
      if (view.delivery === 'never' && r.lastLeadReceived) {return false;}
      if (terms.length) {
        const h = haystack(r);
        if (!terms.every((t) => h.includes(t))) {return false;}
      }
      return true;
    });
  }, [rows, view.q, view.client, view.issue, view.freq, view.delivery]);

  const counts = useMemo(() => ({
    all: baseFiltered.length,
    error: baseFiltered.filter((r) => r.health === 'error').length,
    warn: baseFiltered.filter((r) => r.health === 'warn').length,
    ok: baseFiltered.filter((r) => r.health === 'ok').length,
    disabled: baseFiltered.filter((r) => !r.enabled).length,
  }), [baseFiltered]);

  const visibleRows = useMemo(() => {
    const list = baseFiltered.filter((r) => {
      if (view.tab === 'all') {return true;}
      if (view.tab === 'disabled') {return !r.enabled;}
      return r.health === view.tab;
    });
    const get = SORTS[view.sortKey].get;
    const mul = view.sortDir === 'asc' ? 1 : -1;
    const byName = (a: AutomationRow, b: AutomationRow) =>
      (a.client || '').localeCompare(b.client || '') || rowName(a).localeCompare(rowName(b));
    return [...list].sort((a, b) => {
      const va = get(a), vb = get(b);
      const c = typeof va === 'number' && typeof vb === 'number' ? va - vb : String(va).localeCompare(String(vb));
      return c * mul || byName(a, b);
    });
  }, [baseFiltered, view.tab, view.sortKey, view.sortDir]);

  const pageSize = view.pageSize > 0 ? view.pageSize : Math.max(visibleRows.length, 1);
  const pageCount = Math.max(1, Math.ceil(visibleRows.length / pageSize));
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = visibleRows.slice(safePage * pageSize, safePage * pageSize + pageSize);

  // Group the current page by client (keeps sort order inside each group).
  const groups = useMemo(() => {
    if (view.groupBy !== 'client') {return [{ key: '', rows: pageRows }];}
    const m = new Map<string, AutomationRow[]>();
    pageRows.forEach((r) => { const k = r.client || '(none)'; m.set(k, [...(m.get(k) ?? []), r]); });
    return Array.from(m.entries()).sort((a, b) => a[0].localeCompare(b[0])).map(([key, rs]) => ({ key, rows: rs }));
  }, [pageRows, view.groupBy]);

  const toggleExpanded = (name: string) => setExpanded((s) => {
    const n = new Set(s); if (n.has(name)) {n.delete(name);} else {n.add(name);} return n;
  });
  const toggleGroup = (key: string) => setCollapsedGroups((s) => {
    const n = new Set(s); if (n.has(key)) {n.delete(key);} else {n.add(key);} return n;
  });
  const onHeaderSort = (key: SortKey) => {
    if (view.sortKey === key) {patchView({ sortDir: view.sortDir === 'asc' ? 'desc' : 'asc' });}
    else {patchView({ sortKey: key, sortDir: key === 'today' || key === 'last' ? 'desc' : 'asc' });}
  };

  if (!isSuperAdmin) {
    return (
      <div style={{ padding: 24 }}>
        <h1 className="page-title">Automations</h1>
        <p style={{ color: '#9ca3af' }}>This board is restricted to Super Admins.</p>
      </div>
    );
  }

  const SortHead = ({ k, children, align }: { k: SortKey; children: React.ReactNode; align?: 'right' }) => {
    const active = view.sortKey === k;
    return (
      <th style={{ ...headCell, cursor: 'pointer', textAlign: align ?? 'left', color: active ? C.text : C.sub }} onClick={() => onHeaderSort(k)} title={`Sort by ${SORTS[k].label}`}>
        {children}
        <span style={{ marginLeft: 4, fontSize: 9, opacity: active ? 1 : 0.3 }}>{active ? (view.sortDir === 'asc' ? '▲' : '▼') : '↕'}</span>
      </th>
    );
  };

  const COLS = 6;
  const renderRow = (r: AutomationRow) => {
    const isOpen = expanded.has(r.jobName);
    const hc = HEALTH_COLOR[r.health];
    const flagColor = r.health === 'error' ? '#f87171' : '#fbbf24';
    return (
      <React.Fragment key={r.jobName}>
        <tr
          style={{ background: isOpen ? '#1f2330' : undefined, opacity: r.enabled ? 1 : 0.6, cursor: 'pointer' }}
          onClick={() => toggleExpanded(r.jobName)}
          onMouseEnter={(e) => { if (!isOpen) {e.currentTarget.style.background = '#1c2029';} }}
          onMouseLeave={(e) => { if (!isOpen) {e.currentTarget.style.background = '';} }}
        >
          <td style={{ ...cell, boxShadow: `inset 3px 0 0 ${hc}` }} onClick={(e) => e.stopPropagation()}>
            <div style={{ marginBottom: 6 }}><Badge color={hc}>{HEALTH_LABEL[r.health]}</Badge></div>
            <EnabledSwitch on={r.enabled} busy={busyName === r.jobName} onToggle={() => { void toggleEnabled(r); }} />
          </td>
          <td style={cell}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ color: C.dim, fontSize: 10, width: 10, flexShrink: 0 }}>{isOpen ? '▾' : '▸'}</span>
              <div style={{ minWidth: 0 }}>
                <div style={{ ...ellipsis, fontWeight: 600 }} title={rowName(r)}>{rowName(r)}</div>
                <div style={{ ...ellipsis, fontSize: 11, color: C.sub }} title={`${r.client} · ${r.jobName}`}>
                  {view.groupBy !== 'client' && <span style={{ color: '#a5b4fc' }}>{r.client}</span>}
                  {view.groupBy !== 'client' && ' · '}
                  <span style={{ fontFamily: 'monospace' }}>{r.jobName}</span>
                </div>
              </div>
            </div>
          </td>
          <td style={cell}>
            <div style={ellipsis} title={r.topic || r.mode}>{r.topic || <span style={{ color: C.sub }}>{r.mode}</span>}</div>
            <div style={{ ...ellipsis, fontSize: 11, color: C.sub }} title={filterSummary(r)}>{filterSummary(r)}</div>
          </td>
          <td style={{ ...cell, textAlign: 'right' }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: (r.leadsToday ?? 0) > 0 ? '#4ade80' : C.dim }}>{statsPending ? '…' : fmtNum(r.leadsToday)}</div>
            <div style={{ ...ellipsis, fontSize: 11, color: C.sub }} title={r.lastLeadReceived ?? 'never'}>{fmtWhen(r.lastLeadReceived)}</div>
          </td>
          <td style={cell}>
            {r.flags.length === 0
              ? <span style={{ color: C.dim, fontSize: 12 }}>—</span>
              : (
                <div title={r.flags.join('\n')}>
                  <div style={{ ...ellipsis, fontSize: 12, color: flagColor }}>{r.flags[0]}</div>
                  {r.flags.length > 1 && <div style={{ fontSize: 11, color: C.sub }}>+{r.flags.length - 1} more</div>}
                </div>
              )}
          </td>
          <td style={{ ...cell, textAlign: 'right', whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
            <button className="btn btn-secondary" style={{ fontSize: 12, padding: '4px 10px', marginRight: 6 }} onClick={() => setEditing(r)}>Edit</button>
            <button className="btn btn-secondary" style={{ fontSize: 12, padding: '4px 10px' }} title="Run a 1-lead test of this job" onClick={() => setRunningJob(r.jobName)}>Test</button>
          </td>
        </tr>
        {isOpen && (
          <tr style={{ background: '#1f2330' }}>
            <td colSpan={COLS} style={{ ...cell, padding: '8px 16px 14px 28px', boxShadow: `inset 3px 0 0 ${hc}` }}>
              <RowDetails r={r} />
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  return (
    <div style={{ padding: 24 }}>
      <div className="page-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0, flex: '1 1 400px' }}>
          <h1 className="page-title">Automations</h1>
          <p className="page-subtitle" style={{ color: '#9ca3af' }}>
            Every RetargetIQ pull job: filters, schedule, destination and delivery status. Edits here drive the next pull.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {statsPending && <span style={{ fontSize: 12, color: '#f59e0b' }}>⟳ counts updating…</span>}
          <button className="btn btn-secondary" onClick={() => { void load(); }} disabled={loading}>{loading ? 'Refreshing…' : 'Refresh'}</button>
          <button className="btn btn-primary" onClick={() => setAdding(true)}>+ New automation</button>
        </div>
      </div>

      {error && <div style={{ color: '#ef4444', margin: '12px 0' }}>{error}</div>}
      {loading && !data && <div style={{ color: '#9ca3af', marginTop: 16 }}>Loading automations…</div>}

      {data && (
        <div style={{ marginTop: 16, border: '1px solid #2a2f3a', borderRadius: 12, background: C.panel, overflow: 'hidden' }}>
          {/* Status cards double as the primary filter */}
          <div style={{ display: 'flex', gap: 10, padding: 14, flexWrap: 'wrap', borderBottom: `1px solid ${C.line}` }}>
            <StatusCard label="All automations" count={counts.all} color="#818cf8" active={view.tab === 'all'} onClick={() => patchView({ tab: 'all' })} />
            <StatusCard label="Errors" count={counts.error} color={HEALTH_COLOR.error} active={view.tab === 'error'} onClick={() => patchView({ tab: 'error' })} />
            <StatusCard label="Needs attention" count={counts.warn} color={HEALTH_COLOR.warn} active={view.tab === 'warn'} onClick={() => patchView({ tab: 'warn' })} />
            <StatusCard label="Healthy" count={counts.ok} color={HEALTH_COLOR.ok} active={view.tab === 'ok'} onClick={() => patchView({ tab: 'ok' })} />
            <StatusCard label="Disabled" count={counts.disabled} color="#9ca3af" active={view.tab === 'disabled'} onClick={() => patchView({ tab: 'disabled' })} />
          </div>

          {/* Toolbar */}
          <div style={{ display: 'flex', gap: 8, padding: '12px 14px', flexWrap: 'wrap', alignItems: 'center', borderBottom: `1px solid ${C.line}` }}>
            <div style={{ position: 'relative', flex: '1 1 260px', minWidth: 200 }}>
              <span style={{ position: 'absolute', left: 10, top: 8, color: C.dim, fontSize: 13, pointerEvents: 'none' }}>⌕</span>
              <input
                ref={searchRef}
                style={{ ...ctrl, width: '100%', paddingLeft: 28, paddingRight: 28 }}
                value={view.q}
                placeholder="Search name, client, topic, body, flag…  ( / )"
                onChange={(e) => patchView({ q: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Escape') {patchView({ q: '' });} }}
              />
              {view.q && (
                <button type="button" onClick={() => patchView({ q: '' })} title="Clear search"
                  style={{ position: 'absolute', right: 6, top: 5, background: 'none', border: 'none', color: C.sub, cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>×</button>
              )}
            </div>
            <select style={ctrl} value={view.client} onChange={(e) => patchView({ client: e.target.value })} title="Client">
              <option value="">All clients</option>
              {clientOptions.map(([c, n]) => <option key={c} value={c}>{c} ({n})</option>)}
            </select>
            <select style={{ ...ctrl, maxWidth: 230 }} value={view.issue} onChange={(e) => patchView({ issue: e.target.value })} title="Issue">
              <option value="">Any issue</option>
              {issueOptions.map(([k, n]) => <option key={k} value={k}>{k} ({n})</option>)}
            </select>
            <select style={ctrl} value={view.delivery} onChange={(e) => patchView({ delivery: e.target.value })} title="Delivery">
              <option value="">Any delivery</option>
              <option value="today">Delivered today</option>
              <option value="none">Nothing today</option>
              <option value="never">Never delivered</option>
            </select>
            <select style={ctrl} value={view.freq} onChange={(e) => patchView({ freq: e.target.value })} title="Frequency">
              <option value="">Daily + weekly</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
            </select>
            <div style={{ width: 1, height: 22, background: '#2a2f3a', margin: '0 2px' }} />
            <select style={ctrl} value={view.groupBy} onChange={(e) => patchView({ groupBy: e.target.value as BoardView['groupBy'] })} title="Group by">
              <option value="none">No grouping</option>
              <option value="client">Group by client</option>
            </select>
            <div style={{ display: 'flex' }}>
              <select style={{ ...ctrl, borderTopRightRadius: 0, borderBottomRightRadius: 0 }} value={view.sortKey} onChange={(e) => patchView({ sortKey: e.target.value as SortKey })} title="Sort by">
                {(Object.keys(SORTS) as SortKey[]).map((k) => <option key={k} value={k}>Sort: {SORTS[k].label}</option>)}
              </select>
              <button type="button" onClick={() => patchView({ sortDir: view.sortDir === 'asc' ? 'desc' : 'asc' })} title="Toggle sort direction"
                style={{ ...ctrl, borderLeft: 'none', borderTopLeftRadius: 0, borderBottomLeftRadius: 0, cursor: 'pointer', width: 34 }}>
                {view.sortDir === 'asc' ? '▲' : '▼'}
              </button>
            </div>
            {!viewIsDefault && <button type="button" style={{ ...linkBtn, marginLeft: 4 }} onClick={resetView}>Clear filters</button>}
          </div>

          {/* Table — fixed layout, fits the width (no sideways scrolling); details expand inline */}
          <div style={{ overflowY: 'auto', overflowX: 'auto', maxHeight: 'calc(100vh - 380px)', minHeight: 200 }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'fixed', minWidth: 860 }}>
              <colgroup>
                <col style={{ width: 104 }} />
                <col style={{ width: '30%' }} />
                <col style={{ width: '28%' }} />
                <col style={{ width: 130 }} />
                <col style={{ width: '22%' }} />
                <col style={{ width: 132 }} />
              </colgroup>
              <thead>
                <tr>
                  <SortHead k="status">Status</SortHead>
                  <SortHead k="name">Automation</SortHead>
                  <SortHead k="topic">Topic &amp; filters</SortHead>
                  <SortHead k="today" align="right">Today / last</SortHead>
                  <th style={headCell}>Issues</th>
                  <th style={{ ...headCell, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.length === 0 && (
                  <tr>
                    <td colSpan={COLS} style={{ ...cell, textAlign: 'center', padding: 40, color: C.sub }}>
                      {rows.length === 0 ? 'No automations yet.' : 'No automations match these filters.'}{' '}
                      {rows.length > 0 && <button type="button" style={linkBtn} onClick={resetView}>Clear filters</button>}
                    </td>
                  </tr>
                )}
                {groups.map((g) => {
                  if (view.groupBy !== 'client') {return <React.Fragment key="all">{g.rows.map(renderRow)}</React.Fragment>;}
                  const collapsed = collapsedGroups.has(g.key);
                  const e = g.rows.filter((r) => r.health === 'error').length;
                  const w = g.rows.filter((r) => r.health === 'warn').length;
                  const today = g.rows.reduce((s, r) => s + (r.leadsToday ?? 0), 0);
                  return (
                    <React.Fragment key={g.key}>
                      <tr onClick={() => toggleGroup(g.key)} style={{ cursor: 'pointer', background: '#12151c' }}>
                        <td colSpan={COLS} style={{ ...cell, padding: '8px 12px', position: 'sticky', top: 34 }}>
                          <span style={{ color: C.sub, fontSize: 10, marginRight: 8 }}>{collapsed ? '▸' : '▾'}</span>
                          <b>{g.key}</b>
                          <span style={{ color: C.sub, fontSize: 12, marginLeft: 8 }}>{g.rows.length} job{g.rows.length === 1 ? '' : 's'}</span>
                          <span style={{ display: 'inline-flex', gap: 6, marginLeft: 12, verticalAlign: 'middle' }}>
                            {e > 0 && <Badge color={HEALTH_COLOR.error}>{e} error</Badge>}
                            {w > 0 && <Badge color={HEALTH_COLOR.warn}>{w} attention</Badge>}
                            {e === 0 && w === 0 && <Badge color={HEALTH_COLOR.ok}>all OK</Badge>}
                          </span>
                          <span style={{ float: 'right', fontSize: 12, color: today > 0 ? '#4ade80' : C.dim }}>{statsPending ? '…' : today.toLocaleString()} leads today</span>
                        </td>
                      </tr>
                      {!collapsed && g.rows.map(renderRow)}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Footer: counts + paging */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', padding: '10px 14px', borderTop: `1px solid ${C.line}`, fontSize: 12, color: C.sub }}>
            <div>
              {visibleRows.length === 0 ? 'No results' : `Showing ${(safePage * pageSize + 1).toLocaleString()}–${Math.min(visibleRows.length, (safePage + 1) * pageSize).toLocaleString()} of ${visibleRows.length.toLocaleString()}`}
              {visibleRows.length !== rows.length && ` (filtered from ${rows.length.toLocaleString()})`}
              {' · '}loaded {new Date(data.generatedAt).toLocaleTimeString()}
              {expanded.size > 0 && <> · <button type="button" style={linkBtn} onClick={() => setExpanded(new Set())}>collapse all</button></>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <select style={{ ...ctrl, height: 30, padding: '4px 8px', fontSize: 12 }} value={view.pageSize} onChange={(e) => patchView({ pageSize: Number(e.target.value) })} title="Rows per page">
                {[25, 50, 100].map((n) => <option key={n} value={n}>{n} / page</option>)}
                <option value={0}>All</option>
              </select>
              <button className="btn btn-secondary" style={{ fontSize: 12, padding: '4px 10px' }} disabled={safePage === 0} onClick={() => setPage(safePage - 1)}>‹ Prev</button>
              <span>Page {safePage + 1} of {pageCount}</span>
              <button className="btn btn-secondary" style={{ fontSize: 12, padding: '4px 10px' }} disabled={safePage >= pageCount - 1} onClick={() => setPage(safePage + 1)}>Next ›</button>
            </div>
          </div>
        </div>
      )}

      {data && (
        <div style={{ marginTop: 8, fontSize: 11, color: '#9ca3af' }}>
          “Today” = leads received since midnight Pacific; second line = most recent lead received (both read-only). Click a row for full details.
        </div>
      )}

      {data && <div style={{ marginTop: 16 }}><DestinationsPanel destinations={destinations} onSave={saveDestination} onDelete={removeDestination} /></div>}

      {editing && (
        <EditAutomationModal
          row={editing}
          clients={clientList}
          destinations={destinations}
          onClose={() => setEditing(null)}
          onSave={saveSpec}
          searchTopics={doTopicSearch}
        />
      )}

      {adding && (
        <AddAutomationModal
          clients={clientList}
          destinations={destinations}
          searchTopics={doTopicSearch}
          onClose={() => setAdding(false)}
          onCreate={createSpec}
        />
      )}

      {runningJob && (
        <RunModal
          jobName={runningJob}
          start={startRun}
          poll={pollRun}
          onClose={() => setRunningJob(null)}
        />
      )}
    </div>
  );
}
